import express from "express";
import {
    makeWASocket,
    DisconnectReason,
    useMultiFileAuthState,
    delay,
    Browsers,
    fetchLatestBaileysVersion,
} from "@whiskeysockets/baileys";
import pino from "pino";
import cors from "cors";
import multer from "multer";
import fs from "fs/promises";
import path from "path";
import QRCode from "qrcode";
import axios from "axios";
import dotenv from "dotenv";
import validatePhoneNumber from "validate-phone-number-node-js";
import cron from "node-cron";
import { fileURLToPath } from "url";
import { dirname } from "path";

// Load parent ../.env first (shared Laravel DB_* config), then local .env (overrides).
const __envDir = dirname(fileURLToPath(import.meta.url));
dotenv.config({ path: `${__envDir}/../.env` });
dotenv.config({ path: `${__envDir}/.env`, override: true });

// ESM __dirname polyfill
const __filename = fileURLToPath(import.meta.url);
const __dirname = dirname(__filename);

// ============================================
// LOGGER CONFIGURATION - WIB Timezone
// ============================================

/**
 * Format timestamp to WIB (Asia/Jakarta) timezone
 * @returns {string} Formatted timestamp in WIB
 */
const formatTimestampWIB = () => {
    const now = new Date();
    // Convert to WIB (UTC+7)
    const wibTime = new Date(now.getTime() + (7 * 60 * 60 * 1000));

    const year = wibTime.getUTCFullYear();
    const month = String(wibTime.getUTCMonth() + 1).padStart(2, '0');
    const day = String(wibTime.getUTCDate()).padStart(2, '0');
    const hours = String(wibTime.getUTCHours()).padStart(2, '0');
    const minutes = String(wibTime.getUTCMinutes()).padStart(2, '0');
    const seconds = String(wibTime.getUTCSeconds()).padStart(2, '0');
    const ms = String(wibTime.getUTCMilliseconds()).padStart(3, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${ms} WIB`;
};

/**
 * Custom pino timestamp formatter for WIB
 */
const pinoTime = () => {
    return `,"time":"${formatTimestampWIB()}"`;
};

/**
 * Create logger with WIB timezone and custom format
 */
const createLogger = (options = {}) => {
    const defaultOptions = {
        level: "debug",
        timestamp: pinoTime,
        formatters: {
            level: (label) => {
                return { level: label };
            },
        },
        // Custom serialization for cleaner output
        serializers: {
            err: pino.stdSerializers.err,
            req: pino.stdSerializers.req,
            res: pino.stdSerializers.res,
        },
    };

    return pino({ ...defaultOptions, ...options });
};

const app = express();
const PORT = process.env.PORT || 3001;
const WEBHOOK_URL =
    process.env.LARAVEL_WEBHOOK_URL || "http://localhost:8000/whatsapp/webhook";
const API_TOKEN = process.env.API_TOKEN || null; // Token pour sécuriser les endpoints

// PostgreSQL client
import { query } from "./db.js";
import { sendToWebhookAndRespond } from "./utils/webhook.js";
import { extractSessionId } from "./utils/getWebhookUrl.js";
import {
    getUserSession,
    setUserSession,
    clearUserSession,
    getMenuConfig,
    getMenuItems,
    getUserDeviceSession,
    isSessionExpired,
    getDeviceAutoReadSetting,
} from "./utils/sessionService.js";
import { extractMessageText } from "./utils/extractMessageText.js";
import { markMessageAsRead } from "./utils/markRead.js";
import {
    buildEnhancedWebhookPayload,
} from "./utils/buildWebhookPayload.js";
import { logInboundMessage, forceFlush as forceFlushInbound } from "./utils/inboundMessageLogger.js";
import {
    getSessionDir,
    getSessionsBaseDir,
    safeSaveCreds,
    validateAndRecoverCredentials,
    extractPhoneNumberFromCreds,
    healthCheckAllCredentials,
    cleanupCorruptedSessions,
    cleanupAllSessions,
} from "./utils/credentialsManager.js";

// Configuration
app.use(cors());
app.use(express.json({ limit: "50mb" }));
app.use(express.urlencoded({ extended: true, limit: "50mb" }));

// ============================================
// LOGGER INITIALIZATION
// ============================================

// Main application logger
const logger = createLogger({ level: "debug" });

// Create a separate logger for Baileys socket (less verbose)
const baileysLogger = createLogger({ level: "warn" });
app.use((req, res, next) => {
    logger.info("Request received", {
        method: req.method,
        url: req.url,
        body: req.body,
        headers: req.headers,
    });
    next();
});

// Middleware to check if server is shutting down
app.use((req, res, next) => {
    if (process.isShuttingDown) {
        return res.status(503).json({
            success: false,
            message: "Server is shutting down, please try again later",
        });
    }
    next();
});

// Middleware to verify the API token
app.use((req, res, next) => {
    if (API_TOKEN) {
        const authHeader = req.headers.authorization;
        console.log("🔁 authHeader:", authHeader);
        if (!authHeader || authHeader !== `Bearer ${API_TOKEN}`) {
            logger.warn("Unauthorised access", { url: req.url });
            return res.status(401).json({
                success: false,
                message: "Unauthorised access: invalid token",
            });
        }
    }
    next();
});

// Multer configuration for files
const upload = multer({
    dest: "uploads/",
    limits: {
        fileSize: parseInt(process.env.MAX_FILE_SIZE) || 100 * 1024 * 1024, // 100MB default (increased from 10MB)
    },
});

// Store active sessions
const sessions = new Map();
const sessionData = new Map();

// ============================================
// ACTIVE SESSIONS TRACKING BY DEVICE ID
// Ensures only ONE active session per device ID
// ============================================
// Map<deviceId, sessionId> - tracks which session is currently active for each device
const activeSessionsByDevice = new Map();

// Store for scheduled and bulk messages
const scheduledMessages = new Map(); // storage of programmed messages
const bulkMessageJobs = new Map(); // bulk message job storage

// Store for sent/received messages to support proper quoting
// Structure: Map<messageId, { key, message, timestamp, sessionId }>
const messageStore = new Map();

// ============================================
// NEW: Circuit Breaker for Connection Stability
// Prevents cascading failures and repeated operations on bad connections
// ============================================
const circuitBreakerState = new Map(); // sessionId -> { failures, lastFailureTime, state, cooldownUntil }

// Circuit breaker configuration
const CIRCUIT_CONFIG = {
    failureThreshold: 5, // Open circuit after 5 consecutive failures
    successThreshold: 3, // Close circuit after 3 consecutive successes
    cooldownPeriod: 60000, // Wait 60s before attempting recovery (half-open state)
    timeoutPeriod: 30000, // Consider operations older than 30s as failed
};

// Circuit breaker states
const CIRCUIT_STATE = {
    CLOSED: "closed", // Normal operation
    OPEN: "open", // Failing, reject operations
    HALF_OPEN: "half_open", // Testing if connection recovered
};

/**
 * Check if circuit breaker allows operation for session
 */
const canAttemptOperation = (sessionId) => {
    const breaker = circuitBreakerState.get(sessionId);

    // No breaker state = normal operation
    if (!breaker) return true;

    const now = Date.now();

    // Check if cooldown period has passed
    if (breaker.state === CIRCUIT_STATE.OPEN && now >= breaker.cooldownUntil) {
        console.log(
            `⚡ Circuit breaker HALF_OPEN for ${sessionId} - testing connection`,
        );
        breaker.state = CIRCUIT_STATE.HALF_OPEN;
        breaker.failures = 0;
        circuitBreakerState.set(sessionId, breaker);
        return true;
    }

    // Reject if circuit is OPEN
    if (breaker.state === CIRCUIT_STATE.OPEN) {
        console.warn(
            `🚫 Circuit breaker OPEN for ${sessionId} - rejecting operation (cooldown until ${new Date(breaker.cooldownUntil).toISOString()})`,
        );
        return false;
    }

    return true;
};

/**
 * Record successful operation
 */
const recordSuccess = (sessionId) => {
    const breaker = circuitBreakerState.get(sessionId);

    if (!breaker) return;

    breaker.failures = 0;
    breaker.lastFailureTime = null;

    // If in HALF_OPEN, move to CLOSED after threshold successes
    if (breaker.state === CIRCUIT_STATE.HALF_OPEN) {
        breaker.successCount = (breaker.successCount || 0) + 1;

        if (breaker.successCount >= CIRCUIT_CONFIG.successThreshold) {
            console.log(
                `✅ Circuit breaker CLOSED for ${sessionId} - connection stable`,
            );
            breaker.state = CIRCUIT_STATE.CLOSED;
            breaker.successCount = 0;
        }
    }

    circuitBreakerState.set(sessionId, breaker);
};

/**
 * Record failed operation
 */
const recordFailure = (sessionId) => {
    const breaker = circuitBreakerState.get(sessionId) || {
        failures: 0,
        lastFailureTime: null,
        state: CIRCUIT_STATE.CLOSED,
        cooldownUntil: 0,
        successCount: 0,
    };

    breaker.failures++;
    breaker.lastFailureTime = Date.now();

    // Open circuit if threshold reached
    if (breaker.failures >= CIRCUIT_CONFIG.failureThreshold) {
        breaker.state = CIRCUIT_STATE.OPEN;
        breaker.cooldownUntil = Date.now() + CIRCUIT_CONFIG.cooldownPeriod;
        breaker.successCount = 0;

        console.error(
            `🚨 Circuit breaker OPEN for ${sessionId} - too many failures (${breaker.failures}), cooldown for ${CIRCUIT_CONFIG.cooldownPeriod / 1000}s`,
        );
    }

    circuitBreakerState.set(sessionId, breaker);
};

/**
 * Reset circuit breaker for session
 */
const resetCircuitBreaker = (sessionId) => {
    circuitBreakerState.delete(sessionId);
    console.log(`♻️  Circuit breaker reset for ${sessionId}`);
};

/**
 * Cleanup old session when a new session becomes active for the same device
 * This prevents multiple sessions from interfering with each other
 * @param {string} deviceId - The device ID (without timestamp)
 * @param {string} newSessionId - The new active session ID
 * @param {string} oldSessionId - The old session ID to cleanup
 */
const cleanupOldSession = async (deviceId, newSessionId, oldSessionId) => {
    if (!oldSessionId || oldSessionId === newSessionId) {
        return;
    }

    logger.warn(
        `🧹 Cleaning up old session ${oldSessionId} for device ${deviceId} (new session: ${newSessionId})`,
    );

    try {
        // ============================================
        // CRITICAL: Order matters! Prevent race conditions
        // ============================================

        // Step 1: Get socket and data BEFORE we delete them
        const oldSocket = sessions.get(oldSessionId);
        const oldData = sessionData.get(oldSessionId);

        // Step 2: Remove from sessions map FIRST (before closing socket)
        // This prevents any cron jobs from processing this session
        sessions.delete(oldSessionId);

        // Step 3: Remove from sessionData completely
        // This prevents any future processing of this session
        sessionData.delete(oldSessionId);

        // Step 4: Remove circuit breaker state
        circuitBreakerState.delete(oldSessionId);

        // Step 5: Update activeSessionsByDevice if needed
        if (activeSessionsByDevice.get(deviceId) === oldSessionId) {
            activeSessionsByDevice.set(deviceId, newSessionId);
        }

        // Step 6: Close socket LAST (after all cleanup is done)
        if (oldSocket) {
            try {
                if (oldSocket.ws) {
                    oldSocket.ws.close();
                }
                if (oldSocket.end) {
                    oldSocket.end();
                }
            } catch (closeError) {
                logger.debug(`Error closing old socket: ${closeError.message}`);
            }
        }

        // Step 7: CRITICAL FIX - Delete old session folder from disk
        // This prevents old sessions from being loaded again on server restart
        // and prevents conflicts with WhatsApp
        try {
            const oldSessionDir = getSessionDir(oldSessionId);
            await fs.rm(oldSessionDir, { recursive: true, force: true });
            logger.info(
                `🗑️ Deleted old session folder from disk: ${oldSessionDir}`,
            );
        } catch (deleteError) {
            // Don't fail the cleanup if folder deletion fails
            logger.debug(
                `⚠️ Could not delete old session folder: ${deleteError.message}`,
            );
        }

        logger.info(
            `✅ Old session ${oldSessionId} cleaned up successfully (removed from memory and disk)`,
        );
    } catch (cleanupError) {
        logger.error(
            `❌ Error cleaning up old session ${oldSessionId}:`,
            cleanupError,
        );
    }
};

/**
 * Check and cleanup any old sessions for the same device
 * Called when a new session becomes active
 * @param {string} deviceId - The device ID (without timestamp)
 * @param {string} currentSessionId - The current active session ID
 */
const checkAndCleanupOldSessions = async (deviceId, currentSessionId) => {
    // Get the currently registered active session for this device
    const registeredSessionId = activeSessionsByDevice.get(deviceId);

    logger.debug(
        `📝 checkAndCleanupOldSessions: device=${deviceId}, current=${currentSessionId}, registered=${registeredSessionId || "none"}`
    );

    // If no session registered, register this one and return
    if (!registeredSessionId) {
        activeSessionsByDevice.set(deviceId, currentSessionId);
        logger.info(
            `📝 Registered ${currentSessionId} as active session for device ${deviceId} (no previous session)`
        );
        return;
    }

    // If the registered session is the same as current, check for OTHER old sessions
    if (registeredSessionId === currentSessionId) {
        // ============================================
        // CRITICAL: Even if registered session matches, check for OTHER old sessions
        // This handles case where user scans QR multiple times
        // ============================================
        let foundOldSession = null;

        for (const [sessionId, socket] of sessions.entries()) {
            const sessionDeviceId = extractSessionId(sessionId);

            // Skip different devices
            if (sessionDeviceId !== deviceId) {
                continue;
            }

            // Skip the current session
            if (sessionId === currentSessionId) {
                continue;
            }

            // Found an old session for the same device!
            foundOldSession = sessionId;
            break;
        }

        // If found old session, clean it up
        if (foundOldSession) {
            logger.warn(
                `⚠️  Found old session ${foundOldSession} for device ${deviceId} (while current is ${currentSessionId}), cleaning up...`,
            );
            await cleanupOldSession(deviceId, currentSessionId, foundOldSession);
        }

        // Make sure current session is registered as active
        activeSessionsByDevice.set(deviceId, currentSessionId);
        logger.debug(
            `📝 Re-registered ${currentSessionId} as active session for device ${deviceId} (already registered)`
        );
        return;
    }

    // There's a different registered session - clean it up
    await cleanupOldSession(deviceId, currentSessionId, registeredSessionId);

    // Register new session as the active one
    activeSessionsByDevice.set(deviceId, currentSessionId);
    logger.info(
        `📝 Registered ${currentSessionId} as new active session for device ${deviceId} (replaced old: ${registeredSessionId})`
    );
};

// Cache for group metadata to prevent redundant requests
// Structure: Map<groupId, GroupMetadata>
const groupMetadataCache = new Map();

// Utility to ensure session directory exists
const ensureSessionDir = async (sessionId) => {
    const dir = getSessionDir(sessionId);
    try {
        await fs.mkdir(dir, { recursive: true });
        logger.debug(`Session directory created: ${dir}`);
        return dir;
    } catch (error) {
        logger.error(`Error creating directory ${dir}:`, error);
        throw error;
    }
};

// Validate phone number
const validateWhatsAppNumber = (phone) => {
    const cleaned = phone.replace(/[^0-9+]/g, "");
    if (!validatePhoneNumber.validate(cleaned)) {
        return null;
    }
    return cleaned.includes("@s.whatsapp.net")
        ? cleaned
        : `${cleaned}@s.whatsapp.net`;
};

// Validate group ID
const validateGroupId = (groupId) => {
    const cleaned = groupId.trim();

    // Group IDs end with @g.us
    if (cleaned.includes("@g.us")) {
        return cleaned;
    }

    // If it's just the group ID number, append @g.us
    return `${cleaned}@g.us`;
};

// Helper function to parse mentions from text
// Supports format: @1234567890@s.whatsapp.net or @phoneNumber
const parseMentions = (text) => {
    const mentions = [];
    const mentionRegex = /@(\d+@s\.whatsapp\.net|\d+)/g;
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
        const jid = match[1].includes("@")
            ? match[1]
            : `${match[1]}@s.whatsapp.net`;
        mentions.push(jid);
    }

    return mentions;
};

// Helper function to create quoted message reference for Baileys
const createQuotedMessage = (remoteJid, messageId, fromMe = false) => {
    return {
        key: {
            remoteJid: remoteJid,
            id: messageId,
            fromMe: fromMe,
            participant: remoteJid.includes("@g.us") ? remoteJid : undefined,
        },
        message: {
            conversation: "‎", // Empty message for quote preview
        },
    };
};

function buildMainMenuMessage(menuItems) {
    let text = "*📋 Silakan pilih layanan:*\n\n";
    for (const item of menuItems) {
        text += `*${item.option_key}.* ${item.label}\n`;
    }
    text += `\nKetik nomor atau nama layanan.`;
    return text;
}

// Function to update device status in the database
const updateDeviceStatus = async (sessionDeviceId, status) => {
    const allowedStatuses = ["connected", "connecting", "disconnected"];
    if (!allowedStatuses.includes(status)) {
        throw new Error(`Invalid status: ${status}`);
    }

    // ============================================
    // CRITICAL FIX: Prevent old sessions from marking device as disconnected
    // when there are newer active sessions for the same device
    // ============================================
    if (status === "disconnected") {
        // Method 1: Check using activeSessionsByDevice (most efficient)
        const registeredActiveSessionId = activeSessionsByDevice.get(sessionDeviceId);

        logger.debug(
            `🔍 updateDeviceStatus check: ${sessionDeviceId} -> ${status}, registeredSession: ${registeredActiveSessionId || "none"}`
        );

        if (registeredActiveSessionId) {
            // Verify the registered session is still actually active
            const activeSocket = sessions.get(registeredActiveSessionId);
            const activeSessionData = sessionData.get(registeredActiveSessionId);

            // ============================================
            // CRITICAL: Check both "connected" AND "connecting" status
            // A connecting session should prevent old sessions from marking device as disconnected
            // ============================================
            if (activeSocket && activeSessionData &&
                (activeSessionData.status === "connected" || activeSessionData.status === "connecting")) {
                logger.warn(
                    `⚠️  Skipping disconnect for ${sessionDeviceId} - registered active session: ${registeredActiveSessionId} (status: ${activeSessionData.status})`
                );
                logger.warn(
                    `   Device will remain 'connected' due to active session(s)`
                );
                return { data: null, skipped: true };
            } else {
                // Registered session is no longer active, clear it
                activeSessionsByDevice.delete(sessionDeviceId);
            }
        }

        // Method 2: Fallback - Check all sessions (for edge cases)
        let hasOtherActiveSessions = false;
        let activeSessionId = null;

        for (const [sessionId, socket] of sessions.entries()) {
            // Extract device ID from session ID
            const deviceId = extractSessionId(sessionId);

            // Skip sessions with different device IDs
            if (deviceId !== sessionDeviceId) {
                continue;
            }

            // Check if this session is marked as "replaced" - skip it
            const session = sessionData.get(sessionId);
            if (session && session.status === "replaced") {
                logger.debug(
                    `Skipping replaced session ${sessionId} in disconnect check`
                );
                continue;
            }

            // ============================================
            // CRITICAL: Check both "connected" AND "connecting" status
            // A connecting session should prevent disconnect updates
            //
            // CRITICAL FIX: Don't rely solely on socket.ws existence!
            // Session can be active even if ws is undefined (Baileys quirk)
            // Check if session has user (authenticated) and is marked as connected
            // ============================================
            const isActuallyConnected =
                // Method 1: WebSocket is explicitly OPEN (ideal case)
                (socket.ws && socket.ws.readyState === 1) ||
                // Method 2: Has user auth OR phoneNumber (proves authentication) AND marked as connected
                ((socket.user || session?.phoneNumber) && session && (session.status === "connected" || session.status === "connecting"));

            if (socket && isActuallyConnected && session && (session.status === "connected" || session.status === "connecting")) {
                hasOtherActiveSessions = true;
                activeSessionId = sessionId;
                logger.debug(
                    `✅ Found active session ${sessionId} (ws: ${!!socket.ws}, readyState: ${socket.ws?.readyState}, user: ${!!socket.user}, phoneNumber: ${session?.phoneNumber || "none"}, status: ${session.status})`
                );
                break;
            }
        }

        // If there are other active sessions, don't update to disconnected
        if (hasOtherActiveSessions) {
            logger.warn(
                `⚠️  Skipping disconnect for ${sessionDeviceId} - found active session: ${activeSessionId}`
            );
            logger.warn(
                `   Device will remain 'connected' due to active session(s)`
            );
            return { data: null, skipped: true };
        }
    }

    // ============================================
    // DEBUGGING: Log all "disconnected" status updates with full context
    // ============================================
    if (status === "disconnected") {
        // Create stack trace to identify caller
        const stack = new Error().stack;
        const callerLine = stack.split("\n")[2]?.trim() || "unknown";

        logger.error(
            `🚨🚨🚨 updateDeviceStatus(DISCONNECTED) TRIGGERED 🚨🚨🚨`,
        );
        logger.error(`┌─ Device ID: ${sessionDeviceId}`);
        logger.error(`├─ Status: ${status}`);
        logger.error(`├─ Called from: ${callerLine}`);
        logger.error(`├─ Stack trace:`);
        logger.error(stack.split("\n").slice(2, 6).join("\n"));

        // Check socket state at time of disconnection
        const socket = sessions.get(sessionDeviceId);
        if (socket) {
            logger.error(`├─ Socket exists: YES`);
            logger.error(`├─ socket.ws exists: ${!!socket.ws}`);
            logger.error(
                `├─ socket.ws.readyState: ${socket.ws?.readyState} (${socket.ws?.readyState === 1 ? "OPEN" : socket.ws?.readyState === 3 ? "CLOSED" : "OTHER"})`,
            );
            logger.error(`├─ socket.user exists: ${!!socket.user}`);
            logger.error(`├─ socket.user.id: ${socket.user?.id || "N/A"}`);

            // Check session data
            const session = sessionData.get(sessionDeviceId);
            if (session) {
                logger.error(`├─ SessionData status: ${session.status}`);
                logger.error(
                    `├─ SessionData lastActive: ${session.lastActive ? new Date(session.lastActive).toISOString() : "N/A"}`,
                );
                logger.error(
                    `├─ Time since last active: ${session.lastActive ? `${Math.floor((Date.now() - session.lastActive) / 1000)}s` : "N/A"}`,
                );
            } else {
                logger.error(`├─ SessionData: NOT FOUND`);
            }
        } else {
            logger.error(`├─ Socket exists: NO`);
            logger.error(`├─ Session not in sessions map`);
        }
        logger.error(`└─ Timestamp: ${new Date().toISOString()}`);
        logger.error(`🚨🚨🚨 END DEBUG INFO 🚨🚨🚨\n`);
    } else {
        logger.info(`updateDeviceStatus: ${sessionDeviceId} -> ${status}`);
    }

    try {
        const { rows } = await query(
            `UPDATE devices SET status = $1 WHERE session_device_id = $2 RETURNING *`,
            [status, sessionDeviceId]
        );
        return rows;
    } catch (error) {
        console.error("Failed to update device status:", error.message);
        throw error;
    }
};

// Helper function to handle missing sessions and update status
const handleMissingSession = async (sessionId) => {
    const session_device_id = extractSessionId(sessionId);
    logger.warn(
        `Session ${sessionId} not found, checking before updating device status to disconnected`,
    );

    try {
        // ============================================
        // CRITICAL: Verify socket is ACTUALLY dead before updating database
        // Use async verification that actually pings the socket
        // ============================================
        const isAlive = await verifySocketIsAlive(sessionId);

        if (isAlive) {
            logger.warn(
                `⚠️  Session ${sessionId} socket is still ALIVE (verified via ping), NOT marking as disconnected in database`,
            );

            // Update local sessionData to reflect connected state
            sessionData.set(sessionId, {
                status: "connected",
                lastActive: Date.now(),
            });

            // Update database to CONNECTED since socket is alive
            await updateDeviceStatus(session_device_id, "connected");

            // Update session status
            await updateSessionStatus(sessionId, {
                status: "connected",
            });

            logger.info(
                `✅ Recovered session ${sessionId} - socket was still alive`,
            );
            return;
        }

        logger.info(
            `✅ Confirmed socket is dead for ${sessionId} (ping failed), updating device status to disconnected`,
        );

        // Update device status to disconnected
        await updateDeviceStatus(session_device_id, "disconnected");

        // Update session status to disconnected
        await updateSessionStatus(sessionId, {
            status: "disconnected",
        });

        // Clean up session data
        sessionData.delete(sessionId);

        logger.info(`Cleaned up missing session ${sessionId}`);
    } catch (error) {
        logger.error(`Error handling missing session ${sessionId}:`, error);
    }
};

// Helper function to handle sessions that are not connected
const handleDisconnectedSession = async (sessionId, currentStatus) => {
    const session_device_id = extractSessionId(sessionId);
    logger.warn(
        `Session ${sessionId} is not connected (status: ${currentStatus}), checking socket before updating device status to disconnected`,
    );

    try {
        // ============================================
        // CRITICAL: Verify socket is ACTUALLY dead before updating database
        // Use async verification that actually pings the socket
        // ============================================
        const isAlive = await verifySocketIsAlive(sessionId);

        if (isAlive) {
            logger.warn(
                `⚠️  Session ${sessionId} socket is still ALIVE (verified via ping), NOT marking as disconnected in database`,
            );

            // Update local sessionData to reflect connected state
            const data = sessionData.get(sessionId) || {};
            sessionData.set(sessionId, {
                ...data,
                status: "connected",
                lastActive: Date.now(),
            });

            // Update database to CONNECTED since socket is alive
            await updateDeviceStatus(session_device_id, "connected");

            // Update session status
            await updateSessionStatus(sessionId, {
                status: "connected",
            });

            logger.info(
                `✅ Recovered session ${sessionId} - socket was still alive`,
            );
            return;
        }

        logger.info(
            `✅ Confirmed socket is dead for ${sessionId} (status: ${currentStatus}, ping failed), updating device status to disconnected`,
        );

        // Update device status to disconnected
        await updateDeviceStatus(session_device_id, "disconnected");

        // Update session status to disconnected
        await updateSessionStatus(sessionId, {
            status: "disconnected",
        });

        logger.info(
            `Updated disconnected session ${sessionId} status in database`,
        );
    } catch (error) {
        logger.error(
            `Error handling disconnected session ${sessionId}:`,
            error,
        );
    }
};

const updateSessionStatus = async (sessionId, fieldsToUpdate) => {
    if (!sessionId || typeof fieldsToUpdate !== "object") {
        throw new Error(
            "Invalid arguments: sessionId and fieldsToUpdate are required.",
        );
    }

    const keys = Object.keys(fieldsToUpdate);
    if (keys.length === 0) {
        return [];
    }
    const setClause = keys.map((k, i) => `${k} = $${i + 1}`).join(", ");
    const values = keys.map((k) => fieldsToUpdate[k]);
    values.push(sessionId);

    try {
        const { rows } = await query(
            `UPDATE whatsapp_sessions SET ${setClause} WHERE session_id = $${values.length} RETURNING *`,
            values
        );
        return rows;
    } catch (error) {
        console.error("Failed to update session:", error.message);
        throw error;
    }
};

/**
 * CRITICAL: Verify if session socket is actually alive before marking as disconnected
 * This prevents false positives where live sessions are marked as disconnected
 *
 * @param {string} sessionId - Session ID to check
 * @returns {boolean} - true if socket is alive, false if truly dead
 */
const isSocketActuallyAlive = (sessionId) => {
    const socket = sessions.get(sessionId);

    if (!socket) {
        logger.debug(`isSocketActuallyAlive(${sessionId}): No socket found`);
        return false;
    }

    // Check if WebSocket exists and is OPEN
    if (socket.ws && typeof socket.ws.readyState !== "undefined") {
        const readyState = socket.ws.readyState;

        // WebSocket readyState values:
        // 0 = CONNECTING
        // 1 = OPEN (connected and alive)
        // 2 = CLOSING
        // 3 = CLOSED

        const isAlive = readyState === 1; // Only OPEN is considered alive
        logger.debug(
            `isSocketActuallyAlive(${sessionId}): readyState=${readyState} (${["CONNECTING", "OPEN", "CLOSING", "CLOSED"][readyState] || "UNKNOWN"}), isAlive=${isAlive}`,
        );
        return isAlive;
    }

    // If socket.ws is undefined, check other indicators that socket is still alive
    const session = sessionData.get(sessionId);

    // Check session status
    if (session && session.status === "connected") {
        // Check if socket has user (authenticated)
        if (socket.user && socket.user.id) {
            // Check if lastActive was recent (within 5 minutes)
            const timeSinceLastActive = Date.now() - (session.lastActive || 0);
            const RECENT_ACTIVITY_THRESHOLD = 5 * 60 * 1000; // 5 minutes

            if (timeSinceLastActive < RECENT_ACTIVITY_THRESHOLD) {
                logger.debug(
                    `isSocketActuallyAlive(${sessionId}): ws undefined but socket has user and recent activity - CONSIDERED ALIVE`,
                );
                return true;
            }
        }
    }

    logger.debug(
        `isSocketActuallyAlive(${sessionId}): ws undefined and no other signs of life - CONSIDERED DEAD`,
    );
    return false;
};

/**
 * ASYNC version that actually PINGS the socket to verify it's alive
 * Use this before marking as disconnected to be absolutely sure
 *
 * @param {string} sessionId - Session ID to check
 * @returns {Promise<boolean>} - true if socket is alive, false if truly dead
 */
const verifySocketIsAlive = async (sessionId) => {
    logger.debug(
        `🔍 verifySocketIsAlive(${sessionId}): Starting verification...`,
    );

    const socket = sessions.get(sessionId);

    if (!socket) {
        logger.debug(
            `❌ verifySocketIsAlive(${sessionId}): No socket found in sessions map`,
        );
        return false;
    }

    // Log socket state for debugging
    logger.debug(
        `🔍 verifySocketIsAlive(${sessionId}): Socket found, ws exists: ${!!socket.ws}, ws.readyState: ${socket.ws?.readyState}`,
    );

    // First, do the quick sync check
    if (isSocketActuallyAlive(sessionId)) {
        logger.debug(
            `✅ verifySocketIsAlive(${sessionId}): Quick check passed - socket is alive`,
        );
        return true;
    }

    // If quick check failed, try to actually ping the socket
    // This handles the case where ws is undefined but socket still works
    logger.debug(
        `🔍 verifySocketIsAlive(${sessionId}): Quick check failed, attempting actual ping via fetchBlocklist...`,
    );

    try {
        // Try a lightweight operation to verify socket is actually working
        await socket.fetchBlocklist();

        logger.info(
            `✅ verifySocketIsAlive(${sessionId}): Socket PING succeeded - socket is ALIVE even though ws check failed!`,
        );
        logger.info(
            `   This means socket.ws is undefined/null but the socket connection still works!`,
        );
        return true;
    } catch (pingError) {
        logger.debug(
            `❌ verifySocketIsAlive(${sessionId}): Socket ping failed: ${pingError.message}`,
        );
        logger.debug(`   Error details:`, pingError);
        return false;
    }
};

/**
 * Safe version of updateDeviceStatus that verifies socket is dead before marking disconnected
 * Use this to prevent marking live sessions as disconnected
 *
 * @param {string} sessionId - Session ID
 * @param {string} status - Status to set
 */
const safeUpdateDeviceStatus = async (sessionId, status) => {
    const session_device_id = extractSessionId(sessionId);

    // If trying to mark as disconnected, verify socket is actually dead first
    if (status === "disconnected") {
        // Use async verification that actually pings the socket
        const isAlive = await verifySocketIsAlive(sessionId);

        if (isAlive) {
            logger.warn(
                `⚠️  REFUSING to mark ${sessionId} as disconnected - socket is still ALIVE (verified via ping)!`,
            );

            // Instead, mark as connected since socket is alive
            await updateDeviceStatus(session_device_id, "connected");

            // Also update session data
            const data = sessionData.get(sessionId) || {};
            sessionData.set(sessionId, {
                ...data,
                status: "connected",
                lastActive: Date.now(),
            });

            await updateSessionStatus(sessionId, {
                status: "connected",
            });

            return { skipped: true, reason: "Socket still alive (verified)" };
        }

        logger.info(
            `✅ Verified socket is dead for ${sessionId} (ping failed), proceeding to mark as disconnected`,
        );
    }

    // Proceed with normal update
    await updateDeviceStatus(session_device_id, status);
    return { skipped: false };
};

// Utility function to send a message with ENHANCED stability
const sendMessage = async (
    socket,
    to,
    messageContent,
    sessionId,
    options = {},
) => {
    // Check if server is shutting down
    if (process.isShuttingDown) {
        throw new Error("Server is shutting down, cannot send new messages");
    }

    // ============================================
    // CIRCUIT BREAKER: Check if operation is allowed
    // ============================================
    if (!canAttemptOperation(sessionId)) {
        throw new Error(
            "Circuit breaker is OPEN - too many recent failures, please wait",
        );
    }

    // ============================================
    // CONNECTION VALIDATION: Verify socket is connected
    // ============================================
    const session = sessionData.get(sessionId);

    if (!session || session.status !== "connected") {
        // Don't record failure yet - session status might be out of sync
        // Check if socket is actually alive first
        if (isSocketActuallyAlive(sessionId)) {
            logger.warn(
                `⚠️  Session status says '${session?.status}' but socket is OPEN for ${sessionId} - syncing status`,
            );
            // Sync the status
            sessionData.set(sessionId, {
                ...session,
                status: "connected",
                lastActive: Date.now(),
            });
        } else {
            logger.warn(
                `Session ${sessionId} not connected (status: ${session?.status || "unknown"})`,
            );
        }
        throw new Error(
            `Session not connected (status: ${session?.status || "unknown"})`,
        );
    }

    // Verify socket exists and is usable
    if (!socket) {
        throw new Error("Socket not available");
    }

    // Check if socket's WebSocket connection is alive
    if (!socket.ws || socket.ws.readyState !== 1) {
        // 1 = OPEN
        logger.warn(
            `WebSocket not ready for ${sessionId} (readyState: ${socket.ws?.readyState})`,
        );

        // Try to ping the socket to verify
        try {
            await socket.fetchBlocklist(); // Lightweight ping
            logger.info(
                `✅ Socket ping succeeded for ${sessionId} despite readyState check`,
            );
        } catch (pingError) {
            // Only record failure if socket is truly dead
            if (!isSocketActuallyAlive(sessionId)) {
                recordFailure(sessionId);
            }
            logger.error(
                `Socket ping failed for ${sessionId}:`,
                pingError.message,
            );
            throw new Error(
                "Connection not ready - WebSocket is closed or error",
            );
        }
    }

    // Verify last activity is recent (within 2 minutes)
    const timeSinceLastActive = Date.now() - (session.lastActive || 0);
    const STALE_CONNECTION_THRESHOLD = 120000; // 2 minutes

    if (timeSinceLastActive > STALE_CONNECTION_THRESHOLD) {
        logger.warn(
            `⚠️  Connection stale for ${sessionId} (${Math.round(timeSinceLastActive / 1000)}s since last activity)`,
        );

        // Try proactive ping to wake up connection
        try {
            logger.info(
                `🔔 Proactive ping before message send for ${sessionId}`,
            );
            await socket.fetchBlocklist();

            // Update lastActive
            sessionData.set(sessionId, {
                ...session,
                lastActive: Date.now(),
            });

            logger.info(`✅ Connection restored for ${sessionId}`);
        } catch (pingError) {
            // Only record failure if socket is truly dead
            if (!isSocketActuallyAlive(sessionId)) {
                recordFailure(sessionId);
            }
            logger.error(
                `Failed to restore connection for ${sessionId}:`,
                pingError,
            );
            throw new Error("Connection stale and cannot be restored");
        }
    }

    try {
        // ============================================
        // DYNAMIC TIMEOUT BASED ON FILE SIZE
        // ============================================
        // Calculate timeout based on message content size
        let timeoutMs = 60000; // Default 60s for text messages

        if (messageContent.image || messageContent.video || messageContent.document || messageContent.audio) {
            const mediaBuffer = messageContent.image || messageContent.video || messageContent.document || messageContent.audio;
            const bufferSize = mediaBuffer?.length || 0;
            const bufferMB = bufferSize / (1024 * 1024);

            // Calculate timeout: 30 seconds per MB, minimum 60s, maximum 10 minutes
            // Formula: base 60s + (30s × MB) = untuk 15MB = 60 + (30×15) = 510s (8.5 min)
            timeoutMs = Math.min(
                Math.max(60000, 30000 * bufferMB + 60000),
                600000 // Maximum 10 minutes
            );

            logger.info(
                `📎 Media upload timeout calculated: ${Math.round(timeoutMs / 1000)}s for file size ${bufferMB.toFixed(2)}MB`
            );
        }

        // Add timeout to message send
        const sendPromise = socket.sendMessage(to, messageContent, options);
        const timeoutPromise = new Promise((_, reject) => {
            setTimeout(
                () => reject(new Error(`Message send timeout after ${Math.round(timeoutMs / 1000)}s`)),
                timeoutMs,
            );
        });

        const result = await Promise.race([sendPromise, timeoutPromise]);

        // ============================================
        // SUCCESS: Record success and update circuit breaker
        // ============================================
        recordSuccess(sessionId);

        // Store outgoing message for quoting purposes
        const messageId = result.key.id;
        if (messageId) {
            // Ensure fromMe is set correctly for outgoing messages
            const storedKey = {
                ...result.key,
                fromMe: true, // Explicitly set this for our sent messages
            };

            messageStore.set(messageId, {
                key: storedKey,
                message: messageContent,
                sessionId,
                timestamp: Date.now(),
            });

            // Clean up old messages from store
            if (messageStore.size > 5000) {
                const oldestKey = messageStore.keys().next().value;
                messageStore.delete(oldestKey);
            }
        }

        const data = sessionData.get(sessionId);
        sessionData.set(sessionId, { ...data, lastActive: Date.now() });
        return result;
    } catch (error) {
        // ============================================
        // SMART FAILURE HANDLING: Only record failure if socket is truly dead
        // ============================================
        const errorMsg = error.message || "";
        const isSocketDead = !isSocketActuallyAlive(sessionId);

        // Log the error
        logger.error(`Error sending message to ${to}:`, {
            message: errorMsg,
            socketAlive: isSocketActuallyAlive(sessionId),
            readyState: sessions.get(sessionId)?.ws?.readyState,
        });

        // Enhanced error handling for common issues
        if (errorMsg.includes("timed out")) {
            // Timeout might be temporary - check if socket is still alive
            if (isSocketDead) {
                recordFailure(sessionId);
            } else {
                logger.warn(
                    `⚠️  Message timeout but socket still alive for ${sessionId} - NOT recording failure`,
                );
            }
            throw new Error(
                "Message send timeout - connection may be unstable",
            );
        } else if (errorMsg.includes("401")) {
            // Session expired - socket will be dead soon anyway
            recordFailure(sessionId);
            throw new Error("Session expired - please reconnect");
        } else if (errorMsg.includes("428")) {
            // Rate limiting - NOT a socket failure, don't record it
            logger.warn(
                `⚠️  Rate limited (428) for ${sessionId} - NOT recording failure (temporary)`,
            );
            throw new Error(
                "Too many requests - please wait before sending again",
            );
        } else if (errorMsg.includes("Circuit breaker")) {
            // Circuit blocker error - don't record again
            throw error;
        } else {
            // For other errors, only record if socket is actually dead
            if (isSocketDead) {
                recordFailure(sessionId);
                logger.error(
                    `Socket is dead for ${sessionId}, recording failure`,
                );
            } else {
                logger.warn(
                    `⚠️  Error occurred but socket still alive for ${sessionId} - NOT recording failure`,
                );
            }
        }

        throw error;
    }
};

// Function for processing bulk messages
const processBulkMessages = async (
    jobId,
    sessionId,
    contacts,
    message,
    options = {},
) => {
    const job = bulkMessageJobs.get(jobId);
    if (!job) return;

    const {
        delay: messageDelay = 1000,
        type = "text",
        caption,
        mediaBuffer,
        mimetype,
        fileName,
        mentions,
    } = options;
    const socket = sessions.get(sessionId);

    if (!socket) {
        logger.error(
            `Session ${sessionId} not found for the bulk job ${jobId}`,
        );
        job.status = "failed";
        job.error = "Session not found";

        // Update device status since session is missing
        await handleMissingSession(sessionId);
        return;
    }

    const data = sessionData.get(sessionId);
    if (data?.status !== "connected") {
        logger.error(
            `Session ${sessionId} not connected for the bulk job ${jobId}`,
        );
        job.status = "failed";
        job.error = "Session non found";
        await handleDisconnectedSession(sessionId, data?.status || "unknown");
        return;
    }

    job.status = "running";
    job.startedAt = Date.now();

    for (let i = 0; i < contacts.length; i++) {
        if (job.status === "cancelled") {
            logger.info(`Job bulk ${jobId} cancelled`);
            break;
        }

        const contact = contacts[i];
        const formattedNumber = validateWhatsAppNumber(contact);

        if (!formattedNumber) {
            logger.warn(`Invalid number ignored: ${contact}`);
            job.failed++;
            continue;
        }

        try {
            let messageContent = {};

            // Parse mentions for each contact
            const parsedMentions = mentions || parseMentions(message);

            switch (type) {
                case "text":
                    messageContent = { text: message };
                    // Add mentions if present
                    if (parsedMentions.length > 0) {
                        messageContent = {
                            text: message,
                            contextInfo: {
                                mentionedJid: parsedMentions,
                            },
                        };
                    }
                    break;
                case "image":
                    messageContent = {
                        image: mediaBuffer,
                        caption: caption || message,
                    };
                    if (parsedMentions.length > 0) {
                        messageContent.contextInfo = {
                            mentionedJid: parsedMentions,
                        };
                    }
                    break;
                case "audio":
                    messageContent = { audio: mediaBuffer, mimetype };
                    break;
                case "video":
                    messageContent = {
                        video: mediaBuffer,
                        caption: caption || message,
                    };
                    if (parsedMentions.length > 0) {
                        messageContent.contextInfo = {
                            mentionedJid: parsedMentions,
                        };
                    }
                    break;
                case "document":
                    messageContent = {
                        document: mediaBuffer,
                        mimetype,
                        fileName,
                    };
                    break;
                default:
                    throw new Error(`Message type not supported: ${type}`);
            }

            await sendMessage(
                socket,
                formattedNumber,
                messageContent,
                sessionId,
            );
            job.sent++;
            logger.info(
                `Bulk message sent to ${formattedNumber} (${job.sent}/${contacts.length})`,
            );
        } catch (error) {
            logger.error(`Error sending bulk to ${contact}:`, error);
            job.failed++;
        }

        job.processed++;

        // Delay between messages to prevent spam
        if (i < contacts.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, messageDelay));
        }
    }

    job.status = job.failed > 0 ? "completed_with_errors" : "completed";
    job.completedAt = Date.now();

    logger.info(
        `Job bulk ${jobId} completed: ${job.sent} messengers, ${job.failed} failed`,
    );
};

// Fungsi untuk memproses pesan terjadwal
const processScheduledMessage = async (messageId) => {
    const scheduledMsg = scheduledMessages.get(messageId);
    if (!scheduledMsg) return;

    const {
        sessionId,
        to,
        message,
        type = "text",
        options = {},
    } = scheduledMsg;
    const socket = sessions.get(sessionId);

    if (!socket) {
        logger.error(
            `Session ${sessionId} Not found for the scheduled message ${messageId}`,
        );
        scheduledMsg.status = "failed";
        scheduledMsg.error = "Session non trouvée";

        // Update device status since session is missing
        await handleMissingSession(sessionId);
        return;
    }

    const data = sessionData.get(sessionId);
    if (data?.status !== "connected") {
        logger.error(
            `Session ${sessionId} Not found for the scheduled message ${messageId}`,
        );
        scheduledMsg.status = "failed";
        scheduledMsg.error = "Session non connectée";
        await handleDisconnectedSession(sessionId, data?.status || "unknown");
        return;
    }

    const formattedNumber = validateWhatsAppNumber(to);
    if (!formattedNumber) {
        logger.error(`Invalid number for scheduled message${messageId}: ${to}`);
        scheduledMsg.status = "failed";
        scheduledMsg.error = "Number invalid";
        return;
    }

    try {
        let messageContent = {};

        switch (type) {
            case "text":
                messageContent = { text: message };
                break;
            case "image":
                messageContent = {
                    image: options.mediaBuffer,
                    caption: options.caption || message,
                };
                break;
            case "audio":
                messageContent = {
                    audio: options.mediaBuffer,
                    mimetype: options.mimetype,
                };
                break;
            case "video":
                messageContent = {
                    video: options.mediaBuffer,
                    caption: options.caption || message,
                };
                break;
            case "document":
                messageContent = {
                    document: options.mediaBuffer,
                    mimetype: options.mimetype,
                    fileName: options.fileName,
                };
                break;
            default:
                throw new Error(`Message type not supported: ${type}`);
        }

        const result = await sendMessage(
            socket,
            formattedNumber,
            messageContent,
            sessionId,
        );

        scheduledMsg.status = "sent";
        scheduledMsg.sentAt = Date.now();
        scheduledMsg.messageId = result.key.id;

        logger.info(`Message scheduled ${messageId} sent successfully`);
    } catch (error) {
        logger.error(`Error sending scheduled message ${messageId}:`, error);
        scheduledMsg.status = "failed";
        scheduledMsg.error = error.message;
    }
};

// Scheduler to check scheduled messages and cleanup old stored messages
cron.schedule("* * * * *", () => {
    // Check every minute
    const now = Date.now();

    for (const [messageId, scheduledMsg] of scheduledMessages) {
        if (
            scheduledMsg.status === "pending" &&
            scheduledMsg.scheduledAt <= now
        ) {
            processScheduledMessage(messageId);
        }

        // Delete old messages (older than 24 hours)
        if (
            scheduledMsg.status !== "pending" &&
            now - scheduledMsg.createdAt > 24 * 60 * 60 * 1000
        ) {
            scheduledMessages.delete(messageId);
        }
    }

    // Clean up old messages from messageStore (older than 7 days)
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    let cleanedCount = 0;
    for (const [messageId, storedMsg] of messageStore) {
        if (now - (storedMsg.timestamp || 0) > sevenDays) {
            messageStore.delete(messageId);
            cleanedCount++;
        }
    }
    if (cleanedCount > 0) {
        logger.debug(
            `Cleaned up ${cleanedCount} old messages from store. Current size: ${messageStore.size}`,
        );
    }
});

// ============================================
// PROACTIVE CONNECTION MONITORING (Auto-Reconnect)
// Runs every 30 seconds to check and reconnect dead connections
// ============================================
const CONNECTION_CHECK_CONFIG = {
    checkIntervalSeconds: 30, // Check every 30 seconds
    staleThresholdSeconds: 60, // Consider stale if no activity for 60 seconds
    reconnectCooldownMs: 60000, // Wait 60s between reconnect attempts for same session
};

// Track last reconnect attempt time to prevent rapid-fire reconnects
const lastReconnectAttempt = new Map();

/**
 * Proactively reconnect a dead session
 * Returns true if reconnection was attempted
 */
const proactiveReconnectIfDead = async (sessionId) => {
    const socket = sessions.get(sessionId);
    if (!socket) {
        logger.debug(`proactiveReconnectIfDead(${sessionId}): No socket found`);
        return false;
    }

    const data = sessionData.get(sessionId) || {};
    const now = Date.now();

    // ============================================
    // CRITICAL: Skip if session is marked as "replaced"
    // This prevents old sessions from interfering with new ones
    // ============================================
    if (data.status === "replaced") {
        logger.debug(
            `proactiveReconnectIfDead(${sessionId}): Session is replaced, skipping reconnect`,
        );
        return false;
    }

    // ============================================
    // CRITICAL: Skip sessions that never connected (no phoneNumber)
    // These are QR sessions that failed - don't reconnect them automatically
    // ============================================
    if (!data.phoneNumber) {
        // ============================================
        // CRITICAL FIX: Try to recover phoneNumber from credentials file
        // This can happen after server restart when sessionData is lost but credentials exist
        // ============================================
        logger.debug(
            `⚠️  proactiveReconnectIfDead(${sessionId}): No phoneNumber, attempting recovery...`,
        );
        const recoveredPhoneNumber = await recoverPhoneNumberFromCreds(sessionId);

        if (recoveredPhoneNumber) {
            logger.info(
                `✅ proactiveReconnectIfDead(${sessionId}): Recovered phoneNumber, allowing reconnection`,
            );
            // Update sessionData with recovered phoneNumber
            sessionData.set(sessionId, {
                ...data,
                phoneNumber: recoveredPhoneNumber,
            });
        } else {
            logger.debug(
                `proactiveReconnectIfDead(${sessionId}): Never connected (no phoneNumber), skipping reconnect`,
            );
            return false;
        }
    }

    // ============================================
    // CRITICAL: Skip sessions in failed states
    // ============================================
    if (data.status === "qr_timeout_failed" || data.status === "failed") {
        logger.debug(
            `proactiveReconnectIfDead(${sessionId}): Session in failed state (${data.status}), skipping reconnect`,
        );
        return false;
    }

    // Skip if already reconnecting
    if (data.isReconnecting) {
        logger.debug(
            `proactiveReconnectIfDead(${sessionId}): Already reconnecting, skipping`,
        );
        return false;
    }

    // Check cooldown period
    const lastAttempt = lastReconnectAttempt.get(sessionId) || 0;
    if (now - lastAttempt < CONNECTION_CHECK_CONFIG.reconnectCooldownMs) {
        logger.debug(
            `proactiveReconnectIfDead(${sessionId}): Cooldown active, skipping (${Math.round((CONNECTION_CHECK_CONFIG.reconnectCooldownMs - (now - lastAttempt)) / 1000)}s remaining)`,
        );
        return false;
    }

    // Check if connection is stale (no recent activity)
    let lastActive = data.lastActive || data.createdAt || 0;

    // ============================================
    // CRITICAL FIX: Sanity check for lastActive timestamp
    // If lastActive is 0 or unreasonably old (> 24 hours), it's likely invalid
    // Use current time instead to prevent false positive stale detection
    // ============================================
    const ONE_DAY_MS = 24 * 60 * 60 * 1000;
    if (lastActive === 0 || (now - lastActive) > ONE_DAY_MS) {
        logger.warn(
            `⚠️  Invalid lastActive timestamp for ${sessionId}: ${lastActive} (${lastActive === 0 ? 'not set' : Math.round((now - lastActive) / 1000 / 60) + ' minutes ago'})`,
        );
        logger.warn(
            `   This is likely a data issue. Setting lastActive to now and checking socket health...`,
        );
        // Update lastActive to current time
        sessionData.set(sessionId, {
            ...data,
            lastActive: now,
        });
        lastActive = now;
    }

    const timeSinceActive = now - lastActive;

    if (timeSinceActive < CONNECTION_CHECK_CONFIG.staleThresholdSeconds * 1000) {
        // Connection is still active
        return false;
    }

    // Verify socket is actually dead
    let isDead = false;
    try {
        // Quick check first
        if (!isSocketActuallyAlive(sessionId)) {
            // Double check with actual ping
            await socket.fetchBlocklist();
            // If we got here, socket is actually alive
            logger.debug(
                `proactiveReconnectIfDead(${sessionId}): Socket recovered after check`,
            );
            // Update last active time
            sessionData.set(sessionId, {
                ...data,
                lastActive: now,
            });
            return false;
        }
    } catch (pingError) {
        logger.debug(
            `proactiveReconnectIfDead(${sessionId}): Ping failed, socket is dead: ${pingError.message}`,
        );
        isDead = true;
    }

    if (!isDead) {
        return false;
    }

    // Connection is dead - initiate proactive reconnect
    const session_device_id = extractSessionId(sessionId);

    logger.warn(
        `🔧 Proactive reconnect triggered for ${sessionId} (stale for ${Math.round(timeSinceActive / 1000)}s)`,
    );

    // Mark as reconnecting
    sessionData.set(sessionId, {
        ...data,
        status: "reconnecting",
        isReconnecting: true,
    });

    // Update device status
    await updateDeviceStatus(session_device_id, "reconnecting").catch((err) =>
        logger.error(`Failed to update device status:`, err),
    );

    // Record reconnect attempt
    lastReconnectAttempt.set(sessionId, now);

    try {
        // Close old socket
        if (socket.ws) {
            try {
                socket.ws.close();
            } catch (e) {
                // Ignore
            }
        }
        sessions.delete(sessionId);

        // Small delay before reconnect
        await delay(2000);

        // Create new socket
        const newSocket = await createWhatsAppSocket(sessionId, 0);
        sessions.set(sessionId, newSocket);

        logger.info(`✅ Proactive reconnection successful for ${sessionId}`);

        return true;
    } catch (reconnectError) {
        logger.error(
            `❌ Proactive reconnection failed for ${sessionId}:`,
            reconnectError,
        );

        // Mark as failed but allow retry
        sessionData.set(sessionId, {
            ...data,
            status: "connection_unstable",
            isReconnecting: false,
            lastError: reconnectError.message,
        });

        // ============================================
        // CRITICAL FIX: Remove this failed session from activeSessionsByDevice
        // if it never successfully connected
        // ============================================
        const wasNeverConnected = !data.phoneNumber; // No phoneNumber means never connected
        const registeredSessionId = activeSessionsByDevice.get(session_device_id);

        if (wasNeverConnected && registeredSessionId === sessionId) {
            activeSessionsByDevice.delete(session_device_id);
            logger.debug(
                `🗑️ Removed failed session ${sessionId} from activeSessionsByDevice (never connected)`
            );
        }

        // ============================================
        // CRITICAL: Only update device to disconnected if:
        // 1. This session was previously connected, OR
        // 2. No other sessions are connected for this device
        // ============================================
        let shouldUpdateToDisconnected = true;

        if (wasNeverConnected) {
            // Check if there are other connected sessions for this device
            for (const [otherSessionId, socket] of sessions.entries()) {
                const otherDeviceId = extractSessionId(otherSessionId);
                if (otherDeviceId !== session_device_id) continue;

                const otherSession = sessionData.get(otherSessionId);
                if (otherSession && otherSession.status === "connected" && socket?.ws?.readyState === 1) {
                    shouldUpdateToDisconnected = false;
                    logger.info(
                        `✅ Found active connected session ${otherSessionId} for device ${session_device_id} - will NOT mark as disconnected`
                    );
                    break;
                }
            }
        }

        if (shouldUpdateToDisconnected) {
            await updateDeviceStatus(session_device_id, "disconnected").catch(
                (err) => logger.error(`Failed to update device status:`, err),
            );
        } else {
            logger.info(
                `⚠️ Skipping device status update - device ${session_device_id} still has active connected session(s)`
            );
        }

        return false;
    }
};

// Cron job for connection monitoring - runs every 30 seconds
cron.schedule("*/30 * * * * *", async () => {
    const now = Date.now();
    const sessionIds = Array.from(sessions.keys());

    if (sessionIds.length === 0) {
        return;
    }

    logger.debug(
        `🔍 Connection monitoring check for ${sessionIds.length} sessions`,
    );

    for (const sessionId of sessionIds) {
        const data = sessionData.get(sessionId) || {};

        // ============================================
        // CRITICAL: Skip sessions that are marked as "replaced"
        // This prevents old sessions from interfering with new ones
        // ============================================
        if (data.status === "replaced") {
            continue;
        }

        // ============================================
        // CRITICAL: Skip sessions that never connected (no phoneNumber)
        // These are QR sessions that failed - don't reconnect them automatically
        // ============================================
        if (!data.phoneNumber) {
            // ============================================
            // CRITICAL FIX: Try to recover phoneNumber from credentials file
            // This can happen after server restart when sessionData is lost but credentials exist
            // ============================================
            logger.debug(
                `⚠️  Session ${sessionId} has no phoneNumber, attempting to recover from credentials...`,
            );
            const recoveredPhoneNumber = await recoverPhoneNumberFromCreds(sessionId);

            if (recoveredPhoneNumber) {
                logger.info(
                    `✅ Recovered phoneNumber for ${sessionId}, allowing reconnection`,
                );
                // Update sessionData with recovered phoneNumber
                sessionData.set(sessionId, {
                    ...data,
                    phoneNumber: recoveredPhoneNumber,
                });
            } else {
                logger.debug(
                    `Skipping reconnect for ${sessionId} - never connected (no phoneNumber in credentials)`,
                );
                continue;
            }
        }

        // ============================================
        // CRITICAL: Skip sessions in failed states
        // These sessions should not be reconnected automatically
        // ============================================
        if (data.status === "qr_timeout_failed" ||
            data.status === "failed") {
            logger.debug(`Skipping reconnect for ${sessionId} - in failed state: ${data.status}`);
            continue;
        }

        let lastActive = data.lastActive || data.createdAt || 0;

        // ============================================
        // CRITICAL FIX: Sanity check for lastActive timestamp
        // If lastActive is 0 or unreasonably old (> 24 hours), it's likely invalid
        // Use current time instead to prevent false positive stale detection
        // ============================================
        const ONE_DAY_MS = 24 * 60 * 60 * 1000;
        if (lastActive === 0 || (now - lastActive) > ONE_DAY_MS) {
            logger.warn(
                `⚠️  Invalid lastActive timestamp for ${sessionId}: ${lastActive === 0 ? 'not set' : Math.round((now - lastActive) / 1000 / 60) + ' minutes ago'} - fixing...`,
            );
            // Update lastActive to current time
            sessionData.set(sessionId, {
                ...data,
                lastActive: now,
            });
            lastActive = now;
        }

        const timeSinceActive = now - lastActive;

        // Skip if recently active
        if (timeSinceActive < CONNECTION_CHECK_CONFIG.staleThresholdSeconds * 1000) {
            continue;
        }

        // Attempt proactive reconnect
        await proactiveReconnectIfDead(sessionId);
    }
});

// Function to create WhatsApp socket with retry mechanism
const createWhatsAppSocket = async (sessionId, retryCount = 0) => {
    try {
        // Exponential backoff for retry attempts
        const backoffDelay = Math.min(1000 * Math.pow(2, retryCount), 30000);
        if (retryCount > 0) {
            logger.info(
                `Retrying connection for ${sessionId} after ${backoffDelay}ms (attempt ${retryCount})`,
            );
            await delay(backoffDelay);
        }

        const sessionDir = await ensureSessionDir(sessionId);
        const { state, saveCreds } = await useMultiFileAuthState(sessionDir);
        const { version } = await fetchLatestBaileysVersion();

        const socket = makeWASocket({
            auth: state,
            version,
            printQRInTerminal: false,
            syncFullHistory: false,
            logger: baileysLogger,
            browser: Browsers.macOS("Desktop"), // More stable browser fingerprint
            generateHighQualityLinkPreview: false,

            // ============================================
            // ULTRA-STABLE TIMEOUT CONFIGURATION (OPTIMIZED FOR LARGE FILES)
            // ============================================
            connectTimeoutMs: 240000, // 4 minutes - extended for very slow connections
            qrTimeoutMs: 600000, // 10 minutes - extended QR scanning time
            defaultQueryTimeoutMs: 300000, // 5 minutes - INCREASED for large file uploads (was 120s)
            keepAliveIntervalMs: 10000, // 10 seconds - OPTIMIZED from 15s (more frequent keep-alive)
            phoneWatchTime: 300000, // 5 minutes - increased from 3 minutes

            // Connection stability options
            markOnlineOnConnect: false, // Don't mark online immediately (reduces detection risk)
            fireInitQueries: true, // Fire initialization queries
            shouldSyncHistoryMessage: () => false, // Don't sync history (faster, less data)

            // ============================================
            // AGGRESSIVE RETRY MECHANISMS
            // ============================================
            retryRequestDelayMs: 1000, // Retry failed requests FAST (1s) - was 1500ms
            transactionTimeoutMs: 600000, // Transaction timeout 10 MINUTES - INCREASED for large file uploads (was 120s)

            // ============================================
            // ENHANCED WebSocket stability options (OPTIMIZED)
            // ============================================
            waWebSocketOptions: {
                url:
                    typeof URL !== "undefined"
                        ? new URL("wss://web.whatsapp.com/ws/chat")
                        : "wss://web.whatsapp.com/ws/chat",
                connectTimeoutMs: 180000, // 3 minutes - increased from 120s
                pingIntervalMs: 8000, // 8 seconds - OPTIMIZED from 10s (more frequent ping)
                closeTimeoutMs: 45000, // 45 seconds - increased from 30s

                // CRITICAL: Handle WebSocket errors gracefully
                onHandshakeError: (error) => {
                    console.error(
                        `WebSocket handshake error for ${sessionId}:`,
                        error,
                    );
                    recordFailure(sessionId);
                },
            },

            // ============================================
            // Enhanced message retrieval with caching
            // ============================================
            getMessage: async (key) => {
                return messageStore.get(key.id)?.message;
            },

            // ============================================
            // Enhanced group metadata caching
            // ============================================
            cachedGroupMetadata: async (jid) => {
                return groupMetadataCache.get(jid);
            },

            // ============================================
            // NEW: Connection resilience options
            // ============================================
            // Emit connection updates more frequently
            emitOwnEvents: false, // Reduce noise from own events
        });
        // socket.ev.on("creds.update", saveCreds);
        // ============================================
        // ENHANCED: Use safeSaveCreds with backup & validation
        // This prevents credentials corruption and provides auto-recovery
        // ============================================
        socket.ev.on("creds.update", async () => {
            console.log("💾 creds.update for", sessionId);

            // CRITICAL: Track pairing state to prevent 515 error from deleting fresh creds
            // When creds.update happens after QR scan, it means pairing was successful
            sessionData.set(sessionId, {
                ...sessionData.get(sessionId),
                justPaired: true,
                pairedAt: Date.now(),
            });

            console.log(
                "✅ Pairing detected, marking session as justPaired to protect from 515 cleanup",
            );

            // Use safeSaveCreds dengan auto-backup dan validation
            const saved = await safeSaveCreds(sessionId, saveCreds);

            if (!saved) {
                logger.error(
                    `❌ Failed to save credentials for ${sessionId} - session may be unstable!`,
                );
            }
        });

        // Handle connection updates with ENHANCED stability
        socket.ev.on("connection.update", async (update) => {
            // Top-level try-catch to prevent unhandled rejections
            try {
                const { connection, lastDisconnect, qr, isNewLogin } = update;
                const session_device_id = extractSessionId(sessionId);

                // === Handle QR Code ===
                if (qr) {
                    try {
                        const qrStartTime = Date.now();
                        const qrCode = await QRCode.toDataURL(qr);
                        const qrGenerationTime = Date.now() - qrStartTime;

                        sessionData.set(sessionId, {
                            ...sessionData.get(sessionId),
                            qrCode,
                            status: "qr_ready",
                            qrGeneratedAt: Date.now(),
                        });

                        logger.info({
                            msg: `📱 QR Code generated for session: ${sessionId}`,
                            generationTimeMs: qrGenerationTime,
                            timeoutMs: 600000, // 10 minutes (matches qrTimeoutMs config)
                            expiresAt: new Date(
                                Date.now() + 600000, // 10 minutes
                            ).toISOString(),
                        });
                    } catch (error) {
                        logger.error({
                            msg: `Error generating QR code for ${sessionId}`,
                            error: error?.message || "Unknown error",
                            stack: error?.stack,
                        });
                    }
                }

                // === Handle Disconnect ===
                if (connection === "close") {
                    // Shutting down: skip reconnection so creds on disk stay
                    // intact for recovery on the next boot.
                    if (process.isShuttingDown) {
                        logger.info(
                            `🛑 ${sessionId}: connection closed during shutdown, skipping reconnect`,
                        );
                        return;
                    }
                    const currentSessionData = sessionData.get(sessionId);

                    // ============================================
                    // CRITICAL: Skip if session doesn't exist in sessionData
                    // This means the session was already cleaned up (replaced by new session)
                    // ============================================
                    if (!currentSessionData) {
                        logger.warn(
                            `⚠️  Session ${sessionId} not found in sessionData (already cleaned up), skipping disconnect handling`,
                        );
                        return;
                    }

                    // ============================================
                    // CRITICAL: Skip if this session is marked as "replaced"
                    // This prevents old sessions from updating device status when replaced by new ones
                    // ============================================
                    if (currentSessionData.status === "replaced") {
                        logger.warn(
                            `⚠️  Session ${sessionId} is marked as "replaced", skipping disconnect handling`,
                        );
                        return;
                    }

                    const statusCode =
                        lastDisconnect?.error?.output?.statusCode;
                    const reason =
                        lastDisconnect?.error?.output?.payload ||
                        lastDisconnect?.error;
                    const errorMessage = lastDisconnect?.error?.message || "";

                    console.error("❌ Disconnected:", reason);
                    logger.warn(`Connection closed for ${sessionId}:`, {
                        statusCode,
                        reason,
                        errorMessage,
                    });

                    // Get current reconnection attempts and check if already reconnecting
                    const reconnectionAttempts =
                        currentSessionData.reconnectionAttempts || 0;

                    // Prevent multiple simultaneous reconnect attempts
                    if (currentSessionData.isReconnecting) {
                        logger.warn(
                            `⚠️  Already reconnecting ${sessionId}, skipping duplicate attempt`,
                        );
                        return;
                    }
                    const MAX_RECONNECTION_ATTEMPTS = 10; // Increased from 5 to 10

                    // === SMART ERROR HANDLING ===

                    // 401: Session conflict - needs cleanup and fresh session
                    if (statusCode === 401) {
                        // Detect specific conflict type
                        const conflictType = reason?.type || "unknown";

                        // ============================================
                        // CRITICAL: ALL 401 errors mean session is invalid
                        // Whether device_removed, unknown, or any other type
                        // The proper action is to DISCONNECT, not reconnect
                        // ============================================
                        logger.warn(
                            `📱 401 ${conflictType === "device_removed" ? "Device Removed" : "Session Invalid"} for ${sessionId} - Type: ${conflictType}`,
                        );
                        logger.info(
                            `💡 ${conflictType === "device_removed" ? "User manually removed the device link" : "Session is no longer valid"} - Marking as disconnected`,
                        );

                        // ============================================
                        // CRITICAL: Clean up session FIRST before updating device status
                        // This prevents updateDeviceStatus from finding an active session
                        // and skipping the disconnect update
                        // ============================================

                        // ============================================
                        // CRITICAL FIX: Try to recover credentials before marking as disconnected
                        // This handles case where credentials are corrupted but backup is valid
                        // ============================================
                        logger.info(`🔍 Attempting to recover credentials for ${sessionId} before marking as disconnected...`);
                        const recoveryResult = await validateAndRecoverCredentials(sessionId);

                        if (recoveryResult.valid && recoveryResult.recovered) {
                            logger.warn(
                                `⚠️ Credentials for ${sessionId} were corrupted but recovered from backup - attempting reconnection...`,
                            );

                            // Try to reconnect with recovered credentials
                            sessionData.set(sessionId, {
                                ...sessionData.get(sessionId),
                                isReconnecting: true,
                                reconnectionAttempts: 0,
                            });

                            // Reconnect with recovered credentials
                            setTimeout(() => {
                                createWhatsAppSocket(sessionId).then((newSocket) => {
                                    sessions.set(sessionId, newSocket);
                                    logger.info(
                                        `✅ Reconnected ${sessionId} with recovered credentials`,
                                    );
                                }).catch((err) => {
                                    logger.error(
                                        `❌ Reconnection failed for ${sessionId} even after recovery:`,
                                        err,
                                    );
                                    // If reconnection fails, mark as disconnected
                                    updateDeviceStatus(session_device_id, "disconnected");
                                });
                            }, 2000);

                            return; // Exit, reconnection will handle it
                        }

                        if (!recoveryResult.valid) {
                            logger.error(
                                `❌ Credentials for ${sessionId} are corrupted and no valid backup available`,
                            );
                        } else {
                            // ============================================
                            // CRITICAL FIX: Credentials are valid but got 401
                            // This could be temporary (network glitch) or permanent (logout)
                            // Try to reconnect once before marking as permanently disconnected
                            // ============================================
                            logger.warn(
                                `⚠️ Credentials for ${sessionId} are valid but got 401 - attempting reconnection...`,
                            );

                            const currentReconnectAttempts = sessionData.get(sessionId)?.reconnectionAttempts || 0;
                            const MAX_401_RECONNECT = 1; // Only try once for 401 with valid creds

                            if (currentReconnectAttempts < MAX_401_RECONNECT) {
                                logger.info(
                                    `🔄 Attempting to reconnect ${sessionId} (401 reconnect attempt ${currentReconnectAttempts + 1}/${MAX_401_RECONNECT})...`,
                                );

                                // Update reconnect attempt
                                sessionData.set(sessionId, {
                                    ...sessionData.get(sessionId),
                                    reconnectionAttempts: currentReconnectAttempts + 1,
                                    status: "connecting",
                                });

                                // Try reconnecting after short delay
                                setTimeout(async () => {
                                    try {
                                        logger.info(
                                            `🔄 Creating new socket for ${sessionId} after 401...`,
                                        );
                                        const newSocket = await createWhatsAppSocket(sessionId);
                                        sessions.set(sessionId, newSocket);
                                        logger.info(
                                            `✅ Successfully reconnected ${sessionId} after 401`,
                                        );
                                    } catch (reconnectError) {
                                        logger.error(
                                            `❌ Reconnection failed for ${sessionId} after 401:`,
                                            reconnectError,
                                        );
                                        // If reconnect fails, mark as disconnected
                                        await updateDeviceStatus(
                                            session_device_id,
                                            "disconnected",
                                        ).catch((err) =>
                                            logger.error(
                                                `Failed to update device status:`,
                                                err,
                                            ),
                                        );
                                    }
                                }, 3000); // Wait 3 seconds before reconnect attempt

                                return; // Exit here, reconnection will handle it
                            } else {
                                logger.error(
                                    `❌ Max reconnection attempts reached for ${sessionId} after 401 - session may be logged out`,
                                );
                            }
                        }

                        // CRITICAL FIX: Only delete from activeSessionsByDevice if THIS session
                        // is the registered active session. Don't delete if there's a newer session!
                        const registeredActiveSessionId = activeSessionsByDevice.get(session_device_id);
                        if (registeredActiveSessionId === sessionId) {
                            activeSessionsByDevice.delete(session_device_id);
                            logger.debug(
                                `🗑️ Removed ${sessionId} from activeSessionsByDevice (was the registered active session)`
                            );
                        } else {
                            logger.warn(
                                `⚠️  NOT removing ${sessionId} from activeSessionsByDevice (registered: ${registeredActiveSessionId})`
                            );
                        }

                        sessions.delete(sessionId);
                        sessionData.delete(sessionId);
                        circuitBreakerState.delete(sessionId);

                        // Now safe to update device status (no active sessions will be found)
                        await updateDeviceStatus(
                            session_device_id,
                            "disconnected",
                        ).catch((err) =>
                            logger.error(
                                `Failed to update device status:`,
                                err,
                            ),
                        );

                        await updateSessionStatus(sessionId, {
                            status: "disconnected",
                        }).catch((err) =>
                            logger.error(
                                `Failed to update session status:`,
                                err,
                            ),
                        );

                        logger.info(
                            `✅ Device ${session_device_id} marked as disconnected and session cleaned up (401 ${conflictType})`,
                        );

                        return; // Exit without reconnecting

                        // Set reconnecting flag to prevent duplicate attempts
                        sessionData.set(sessionId, {
                            ...sessionData.get(sessionId),
                            isReconnecting: true,
                        });

                        // Clean up corrupted session files
                        try {
                            await fs.rm(
                                getSessionDir(sessionId),
                                {
                                    recursive: true,
                                    force: true,
                                },
                            );
                            logger.info(
                                `🧹 Cleaned up corrupted session files for ${sessionId}`,
                            );
                        } catch (cleanupError) {
                            logger.error(
                                `Error cleaning up session files:`,
                                cleanupError,
                            );
                        }

                        if (reconnectionAttempts >= MAX_RECONNECTION_ATTEMPTS) {
                            logger.error(
                                `🛑 Max reconnection attempts (${MAX_RECONNECTION_ATTEMPTS}) reached for ${sessionId}`,
                            );
                            logger.error(
                                `📝 Manual intervention required - Please login again from your device`,
                            );
                            sessionData.set(sessionId, {
                                status: "failed",
                                reconnectionAttempts: 0,
                                isReconnecting: false,
                                lastError:
                                    "401 - Max reconnection attempts reached. Please login again.",
                            });
                            return;
                        }

                        // Exponential backoff with jitter: 5s, 10s, 20s, 40s, 80s, max 120s
                        const baseDelay = Math.min(
                            5000 * Math.pow(2, reconnectionAttempts),
                            120000,
                        );
                        const jitter = Math.random() * 2000; // Add 0-2s random jitter
                        const backoffDelay = baseDelay + jitter;

                        logger.info(
                            `⏳ Auto-reconnect in ${Math.round(backoffDelay / 1000)}s for ${sessionId} (attempt ${reconnectionAttempts + 1}/${MAX_RECONNECTION_ATTEMPTS})`,
                        );
                        logger.info(
                            `💡 Tip: Scan QR code now to reconnect faster`,
                        );
                        await delay(backoffDelay);

                        try {
                            logger.info(
                                `🔄 Attempting reconnection for ${sessionId}...`,
                            );
                            sessionData.set(sessionId, {
                                ...sessionData.get(sessionId),
                                reconnectionAttempts: reconnectionAttempts + 1,
                                isReconnecting: true,
                            });
                            const newSocket = await createWhatsAppSocket(
                                sessionId,
                                reconnectionAttempts + 1,
                            );
                            sessions.set(sessionId, newSocket);
                            logger.info(
                                `✅ Reconnection successful for ${sessionId}`,
                            );
                            return;
                        } catch (retryError) {
                            logger.error(
                                `❌ Retry failed for ${sessionId}:`,
                                retryError,
                            );
                            sessionData.set(sessionId, {
                                ...sessionData.get(sessionId),
                                isReconnecting: false,
                            });
                            // Only mark as disconnected if socket is truly dead
                            const isAlive =
                                await verifySocketIsAlive(sessionId);
                            if (!isAlive) {
                                await updateDeviceStatus(
                                    session_device_id,
                                    "disconnected",
                                ).catch((err) =>
                                    logger.error(
                                        `Failed to update device status:`,
                                        err,
                                    ),
                                );
                            } else {
                                logger.warn(
                                    `⚠️  Retry failed but socket still ALIVE for ${sessionId} - not marking as disconnected`,
                                );
                            }
                        }
                    }

                    // 428: Slow down - too many requests
                    if (statusCode === 428) {
                        logger.warn(
                            `⚠️  428 Slow down for ${sessionId} - Too many requests, backing off...`,
                        );

                        // Longer backoff for rate limiting (30s minimum)
                        const backoffDelay =
                            30000 + reconnectionAttempts * 10000;
                        logger.info(
                            `⏳ Rate limit backoff: waiting ${Math.round(backoffDelay / 1000)}s`,
                        );
                        await delay(backoffDelay);
                    }

                    // 515: Stream Errored - session corrupted, needs full replacement
                    if (statusCode === 515) {
                        const currentSessionData =
                            sessionData.get(sessionId) || {};
                        const justPaired =
                            currentSessionData.justPaired || false;
                        const pairedAt = currentSessionData.pairedAt || 0;
                        const timeSincePairing = Date.now() - pairedAt;

                        // CRITICAL: Check if this 515 error is immediately after pairing
                        // "pairing configured successfully, expect to restart the connection"
                        // If we just paired (within 30 seconds), DON'T delete session files!
                        const PAIRING_GRACE_PERIOD = 30000; // 30 seconds grace period after pairing

                        if (
                            justPaired &&
                            timeSincePairing < PAIRING_GRACE_PERIOD
                        ) {
                            logger.warn({
                                msg: `⚠️  515 Stream Errored for ${sessionId} - Detected post-pairing 515 error`,
                                timeSincePairingMs: timeSincePairing,
                                reason: "Baileys needs to restart connection after pairing",
                                action: "NOT deleting session files - will attempt reconnect with existing creds",
                            });

                            // Clear justPaired flag so we don't keep protecting it
                            sessionData.set(sessionId, {
                                ...currentSessionData,
                                justPaired: false,
                                isReconnecting: true,
                            });

                            // Update status but DON'T delete session files
                            await updateDeviceStatus(
                                session_device_id,
                                "reconnecting",
                            ).catch((err) =>
                                logger.error(
                                    `Failed to update device status:`,
                                    err,
                                ),
                            );

                            // Short delay for reconnect (this is expected after pairing)
                            const reconnectDelay = 3000; // 3 seconds
                            logger.info(
                                `⏳ Waiting ${reconnectDelay / 1000}s for connection restart after pairing...`,
                            );

                            await delay(reconnectDelay);

                            // Try reconnecting WITHOUT deleting files
                            try {
                                logger.info(
                                    `🔄 Attempting reconnect for ${sessionId} with existing creds...`,
                                );
                                const newSocket = await createWhatsAppSocket(
                                    sessionId,
                                    0,
                                );
                                sessions.set(sessionId, newSocket);
                                logger.info(
                                    `✅ Reconnection successful for ${sessionId} after pairing`,
                                );
                                return;
                            } catch (retryError) {
                                logger.error(
                                    `❌ Reconnect failed for ${sessionId}:`,
                                    retryError,
                                );
                                // If reconnect still fails, fall through to normal 515 handling
                                // But only delete files if this is NOT the first attempt after pairing
                                if (reconnectionAttempts < 3) {
                                    logger.info(
                                        `Will retry without deleting files (attempt ${reconnectionAttempts + 1}/3 after pairing)`,
                                    );
                                    return;
                                }
                            }
                        } else {
                            // Normal 515 error - not immediately after pairing
                            logger.warn(
                                `⚠️  515 Stream Errored for ${sessionId} - Session corrupted, cleaning up and recreating... (Attempt ${reconnectionAttempts + 1}/${MAX_RECONNECTION_ATTEMPTS})`,
                            );
                            logger.info(
                                `💡 This error requires full session replacement - similar to 401`,
                            );
                        }

                        // Normal 515 handling (delete session files)
                        // Update status - but verify socket is dead first
                        const isAlive = await verifySocketIsAlive(sessionId);

                        if (isAlive) {
                            logger.warn(
                                `⚠️  515 error but socket still ALIVE (verified via ping) for ${sessionId} - marking as reconnecting`,
                            );
                            // Don't mark as disconnected yet, let it recover
                            await updateDeviceStatus(
                                session_device_id,
                                "reconnecting",
                            ).catch((err) =>
                                logger.error(
                                    `Failed to update device status:`,
                                    err,
                                ),
                            );
                        } else {
                            await updateDeviceStatus(
                                session_device_id,
                                "disconnected",
                            ).catch((err) =>
                                logger.error(
                                    `Failed to update device status:`,
                                    err,
                                ),
                            );
                            await updateSessionStatus(sessionId, {
                                status: "disconnected",
                            }).catch((err) =>
                                logger.error(
                                    `Failed to update session status:`,
                                    err,
                                ),
                            );
                        }

                        // Set reconnecting flag to prevent duplicate attempts
                        sessionData.set(sessionId, {
                            ...sessionData.get(sessionId),
                            isReconnecting: true,
                            justPaired: false, // Clear flag
                        });

                        // Clean up corrupted session files - CRITICAL for 515!
                        try {
                            await fs.rm(
                                getSessionDir(sessionId),
                                {
                                    recursive: true,
                                    force: true,
                                },
                            );
                            logger.info(
                                `🧹 Cleaned up corrupted session files for ${sessionId} (515 error)`,
                            );
                        } catch (cleanupError) {
                            logger.error(
                                `Error cleaning up session files:`,
                                cleanupError,
                            );
                        }

                        // Check max attempts
                        if (reconnectionAttempts >= MAX_RECONNECTION_ATTEMPTS) {
                            logger.error(
                                `🛑 Max reconnection attempts (${MAX_RECONNECTION_ATTEMPTS}) reached for ${sessionId}`,
                            );
                            logger.error(
                                `📝 Manual intervention required - Stream error persists`,
                            );
                            sessionData.set(sessionId, {
                                status: "failed",
                                reconnectionAttempts: 0,
                                isReconnecting: false,
                                lastError:
                                    "515 - Stream error. Max reconnection attempts reached. Please login again.",
                            });
                            return;
                        }

                        // Exponential backoff with jitter: 5s, 10s, 20s, 40s, 80s, max 120s
                        const baseDelay = Math.min(
                            5000 * Math.pow(2, reconnectionAttempts),
                            120000,
                        );
                        const jitter = Math.random() * 2000; // Add 0-2s random jitter
                        const backoffDelay = baseDelay + jitter;

                        logger.info(
                            `⏳ Auto-reconnect in ${Math.round(backoffDelay / 1000)}s for ${sessionId} (attempt ${reconnectionAttempts + 1}/${MAX_RECONNECTION_ATTEMPTS})`,
                        );
                        logger.info(
                            `💡 Tip: Scan QR code now to reconnect faster`,
                        );
                        await delay(backoffDelay);

                        // Recreate socket with fresh session
                        try {
                            logger.info(
                                `🔄 Attempting reconnection for ${sessionId} after 515 error...`,
                            );
                            sessionData.set(sessionId, {
                                ...sessionData.get(sessionId),
                                reconnectionAttempts: reconnectionAttempts + 1,
                                isReconnecting: true,
                            });
                            const newSocket = await createWhatsAppSocket(
                                sessionId,
                                reconnectionAttempts + 1,
                            );
                            sessions.set(sessionId, newSocket);
                            logger.info(
                                `✅ Reconnection successful for ${sessionId} after 515 error`,
                            );
                            return;
                        } catch (retryError) {
                            logger.error(
                                `❌ Retry failed for ${sessionId}:`,
                                retryError,
                            );
                            sessionData.set(sessionId, {
                                ...sessionData.get(sessionId),
                                isReconnecting: false,
                            });
                            // Only mark as disconnected if socket is truly dead
                            const isAlive =
                                await verifySocketIsAlive(sessionId);
                            if (!isAlive) {
                                await updateDeviceStatus(
                                    session_device_id,
                                    "disconnected",
                                ).catch((err) =>
                                    logger.error(
                                        `Failed to update device status:`,
                                        err,
                                    ),
                                );
                            } else {
                                logger.warn(
                                    `⚠️  Retry failed but socket still ALIVE for ${sessionId} - not marking as disconnected`,
                                );
                            }
                        }
                    }

                    // 408: QR Timeout - Special handling for QR scanning timeout
                    if (
                        statusCode === DisconnectReason.timedOut ||
                        statusCode === 408
                    ) {
                        const currentSessionData = sessionData.get(sessionId);
                        const qrTimeoutAttempts =
                            currentSessionData?.qrTimeoutAttempts || 0;
                        const MAX_QR_TIMEOUTS = 3; // Allow 3 QR timeout cycles

                        // ============================================
                        // CRITICAL FIX: Check if session was previously connected
                        // If it has phoneNumber, it was connected - treat as temporary disconnect, NOT QR timeout
                        // ============================================
                        const wasPreviouslyConnected = !!currentSessionData?.phoneNumber;

                        if (wasPreviouslyConnected) {
                            logger.warn(
                                `⚠️  Established session disconnected (408) for ${sessionId} - this is a temporary network drop, NOT QR timeout`,
                            );
                            logger.warn(
                                `💡 Session has phone number: ${currentSessionData.phoneNumber} - will auto-reconnect using saved credentials`,
                            );

                            // Don't treat as QR timeout - let it fall through to general reconnect logic
                            // The saved credentials will be used to reconnect without needing QR scan
                        } else {
                            // This is a QR timeout - user didn't scan in time
                            const timeSinceCreation =
                                Date.now() -
                                (currentSessionData?.qrGeneratedAt || Date.now());
                            const actuallyTimedOut = timeSinceCreation > 55000; // Only treat as QR timeout if > 55 seconds elapsed

                            logger.warn(
                                `⏱️  QR Timeout for ${sessionId} (attempt ${qrTimeoutAttempts + 1}/${MAX_QR_TIMEOUTS})`,
                            );
                            logger.warn(
                                `⏱️  Time since QR generation: ${Math.floor(timeSinceCreation / 1000)}s`,
                            );

                        // Only regenerate QR if this was a real QR timeout (user didn't scan in time)
                        // If connection closed quickly (< 55s), it's likely a network issue, not QR timeout
                        if (!actuallyTimedOut) {
                            logger.warn(
                                `⚠️  Connection closed quickly (${Math.floor(timeSinceCreation / 1000)}s) - likely network issue, not QR timeout`,
                            );
                            logger.warn(
                                `⚠️  Will NOT regenerate QR automatically - user should retry manually`,
                            );
                            logger.warn(
                                `⚠️  If this persists, check: internet connection, firewall, or WhatsApp server status`,
                            );

                            // Mark as failed but don't auto-recreate
                            sessionData.set(sessionId, {
                                status: "connection_unstable",
                                reconnectionAttempts: 0,
                                qrTimeoutAttempts: 0,
                                lastError: `Connection closed ${Math.floor(timeSinceCreation / 1000)}s after QR - network issue`,
                            });

                            // ============================================
                            // CRITICAL FIX: Remove this failed session from activeSessionsByDevice
                            // This session never successfully connected, so it shouldn't be considered
                            // the active session for this device
                            // ============================================
                            const registeredSessionId = activeSessionsByDevice.get(session_device_id);
                            if (registeredSessionId === sessionId) {
                                activeSessionsByDevice.delete(session_device_id);
                                logger.debug(
                                    `🗑️ Removed failed session ${sessionId} from activeSessionsByDevice (never connected)`
                                );
                            }

                            // ============================================
                            // CRITICAL: Only update device to disconnected if NO OTHER sessions are connected
                            // Check all sessions for this device before marking as disconnected
                            // ============================================
                            let hasConnectedSession = false;
                            for (const [otherSessionId, socket] of sessions.entries()) {
                                const otherDeviceId = extractSessionId(otherSessionId);
                                if (otherDeviceId !== session_device_id) continue;

                                const otherSession = sessionData.get(otherSessionId);
                                if (otherSession && otherSession.status === "connected" && socket?.ws?.readyState === 1) {
                                    hasConnectedSession = true;
                                    logger.info(
                                        `✅ Found active connected session ${otherSessionId} for device ${session_device_id} - will NOT mark as disconnected`
                                    );
                                    break;
                                }
                            }

                            if (!hasConnectedSession) {
                                // Only update to disconnected if no other connected sessions exist
                                await updateDeviceStatus(
                                    session_device_id,
                                    "disconnected",
                                ).catch((err) =>
                                    logger.error(
                                        `Failed to update device status:`,
                                        err,
                                    ),
                                );
                            } else {
                                logger.info(
                                    `⚠️ Skipping device status update - device ${session_device_id} still has active connected session(s)`
                                );
                            }
                            return;
                        }

                        logger.info(
                            `💡 QR code was not scanned in time. Generating new QR code...`,
                        );

                        if (qrTimeoutAttempts < MAX_QR_TIMEOUTS) {
                            // Update QR timeout counter AND reset reconnection counter
                            sessionData.set(sessionId, {
                                ...sessionData.get(sessionId),
                                qrTimeoutAttempts: qrTimeoutAttempts + 1,
                                reconnectionAttempts: 0, // Reset reconnection counter for QR timeout
                                lastActive: Date.now(),
                            });

                            // Short delay before regenerating QR
                            await delay(2000);

                            // Force reconnect to generate new QR (will be treated as new connection)
                            logger.info(
                                `🔄 Regenerating QR code for ${sessionId}...`,
                            );

                            // CRITICAL: Close old socket and clear timer to prevent leaks
                            const oldSocket = sessions.get(sessionId);
                            if (oldSocket) {
                                try {
                                    if (oldSocket.ws) {
                                        oldSocket.ws.close();
                                    }
                                    logger.debug(
                                        `Closed old socket for ${sessionId} before QR regeneration`,
                                    );
                                } catch (closeError) {
                                    logger.warn(
                                        `Error closing old socket:`,
                                        closeError,
                                    );
                                }
                            }
                            sessions.delete(sessionId);
                        } else {
                            // ============================================
                            // MAX QR TIMEOUT ATTEMPTS REACHED - COMPLETE CLEANUP
                            // This session never connected and user didn't scan QR in time
                            // Completely remove to prevent infinite reconnect loop
                            // ============================================
                            logger.error(
                                `🛑 Max QR timeout attempts (${MAX_QR_TIMEOUTS}) reached for ${sessionId}`,
                            );
                            logger.info(
                                `💡 User may need to restart the session manually`,
                            );

                            // Close socket if exists
                            const oldSocket = sessions.get(sessionId);
                            if (oldSocket) {
                                try {
                                    if (oldSocket.ws) {
                                        oldSocket.ws.close();
                                    }
                                    if (oldSocket.end) {
                                        oldSocket.end();
                                    }
                                } catch (e) {
                                    // Ignore
                                }
                            }

                            // COMPLETE REMOVAL from all Maps
                            sessions.delete(sessionId);
                            sessionData.delete(sessionId);
                            circuitBreakerState.delete(sessionId);

                            // Remove from activeSessionsByDevice
                            const registeredSessionId = activeSessionsByDevice.get(session_device_id);
                            if (registeredSessionId === sessionId) {
                                activeSessionsByDevice.delete(session_device_id);
                                logger.debug(
                                    `🗑️ Removed failed session ${sessionId} from activeSessionsByDevice (max QR timeouts)`
                                );
                            }

                            // Update device to disconnected
                            await updateDeviceStatus(
                                session_device_id,
                                "disconnected",
                            ).catch((err) =>
                                logger.error(
                                    `Failed to update device status:`,
                                    err,
                                ),
                            );

                            logger.info(
                                `🧹 Cleaned up failed session ${sessionId} after max QR timeout attempts`
                            );
                            return;
                        }
                        } // End of else block for QR timeout (session was NOT previously connected)
                    }

                    // === DETERMINE IF SHOULD RECONNECT ===
                    const permanentFailureCodes = [
                        DisconnectReason.loggedOut,
                        401,
                        403, // Forbidden
                    ];

                    const shouldReconnect =
                        !permanentFailureCodes.includes(statusCode);

                    logger.info(`Connection status for ${sessionId}:`, {
                        statusCode,
                        shouldReconnect,
                        reconnectionAttempts: reconnectionAttempts + 1,
                        maxAttempts: MAX_RECONNECTION_ATTEMPTS,
                    });

                    if (shouldReconnect) {
                        // Check max attempts
                        if (reconnectionAttempts >= MAX_RECONNECTION_ATTEMPTS) {
                            logger.error(
                                `🛑 Max reconnection attempts (${MAX_RECONNECTION_ATTEMPTS}) for ${sessionId}`,
                            );
                            sessions.delete(sessionId);
                            sessionData.set(sessionId, {
                                status: "failed",
                                reconnectionAttempts: 0,
                            });
                            // Only mark as disconnected if socket is truly dead
                            const isAlive =
                                await verifySocketIsAlive(sessionId);
                            if (!isAlive) {
                                await updateDeviceStatus(
                                    session_device_id,
                                    "disconnected",
                                ).catch((err) =>
                                    logger.error(
                                        `Failed to update device status:`,
                                        err,
                                    ),
                                );
                            } else {
                                logger.warn(
                                    `⚠️  Max attempts reached but socket still ALIVE for ${sessionId} - not marking as disconnected`,
                                );
                            }
                            return;
                        }

                        logger.info(
                            `🔄 Attempting reconnect for ${sessionId} (${reconnectionAttempts + 1}/${MAX_RECONNECTION_ATTEMPTS})`,
                        );

                        // Smart exponential backoff based on error type
                        let backoffDelay;
                        if (statusCode === 428) {
                            // Rate limiting - longer backoff
                            backoffDelay = Math.min(
                                30000 * Math.pow(1.5, reconnectionAttempts),
                                180000,
                            );
                        } else if (statusCode === 503) {
                            // Service unavailable - moderate backoff
                            backoffDelay = Math.min(
                                10000 * Math.pow(1.5, reconnectionAttempts),
                                60000,
                            );
                        } else {
                            // Network/temporary errors - standard backoff
                            backoffDelay = Math.min(
                                3000 * Math.pow(2, reconnectionAttempts),
                                60000,
                            );
                        }

                        // Add jitter to prevent thundering herd
                        const jitter = Math.random() * 2000;
                        backoffDelay += jitter;

                        logger.info(
                            `⏳ Backoff delay: ${Math.round(backoffDelay / 1000)}s for ${sessionId}`,
                        );
                        await delay(backoffDelay);

                        try {
                            sessionData.set(sessionId, {
                                ...sessionData.get(sessionId),
                                reconnectionAttempts: reconnectionAttempts + 1,
                            });
                            sessions.set(
                                sessionId,
                                await createWhatsAppSocket(
                                    sessionId,
                                    reconnectionAttempts + 1,
                                ),
                            );
                            logger.info(`✅ Auto-reconnected ${sessionId}`);
                        } catch (reconnectError) {
                            logger.error(
                                `❌ Reconnection failed for ${sessionId}:`,
                                reconnectError,
                            );
                            sessions.delete(sessionId);
                            sessionData.set(sessionId, {
                                status: "failed",
                                reconnectionAttempts: 0,
                            });
                            // Only mark as disconnected if socket is truly dead
                            if (!isSocketActuallyAlive(sessionId)) {
                                await updateDeviceStatus(
                                    session_device_id,
                                    "disconnected",
                                ).catch((err) =>
                                    logger.error(
                                        `Failed to update device status:`,
                                        err,
                                    ),
                                );
                            }
                        }
                    } else {
                        // Permanent failure - don't reconnect
                        logger.error(
                            `🛑 Permanent disconnect for ${sessionId} (code: ${statusCode})`,
                        );

                        // CRITICAL: Check if socket is actually alive before marking as disconnected
                        const isAlive = await verifySocketIsAlive(sessionId);

                        if (!isAlive) {
                            sessions.delete(sessionId);
                            sessionData.set(sessionId, {
                                status:
                                    statusCode === DisconnectReason.loggedOut
                                        ? "logged_out"
                                        : "failed",
                                reconnectionAttempts: 0,
                            });
                            await updateSessionStatus(sessionId, {
                                status: "disconnected",
                            }).catch((err) =>
                                logger.error(
                                    `Failed to update session status:`,
                                    err,
                                ),
                            );
                            await updateDeviceStatus(
                                session_device_id,
                                "disconnected",
                            );
                        } else {
                            logger.warn(
                                `⚠️  Permanent disconnect code ${statusCode} but socket still ALIVE (verified via ping) for ${sessionId} - NOT marking as disconnected`,
                            );
                            // Keep session running
                            sessionData.set(sessionId, {
                                status: "connected",
                                lastActive: Date.now(),
                            });
                        }
                    }
                } else if (connection === "connecting") {
                    // Handle connection in progress - monitor for stuck connections
                    const currentData = sessionData.get(sessionId) || {};
                    const connectingStart = currentData.connectingStart || Date.now();
                    const connectingDuration = Date.now() - connectingStart;

                    // Update status to connecting if first time
                    if (!currentData.connectingStart) {
                        sessionData.set(sessionId, {
                            ...currentData,
                            status: "connecting",
                            connectingStart: Date.now(),
                        });
                        logger.info(
                            `🔄 Connecting to WhatsApp for ${sessionId}...`,
                        );

                        // ============================================
                        // CRITICAL: Register this session as the active one for the device
                        // This prevents old sessions from updating device status to "disconnected"
                        // while this session is still connecting
                        // ============================================
                        const deviceId = extractSessionId(sessionId);
                        activeSessionsByDevice.set(deviceId, sessionId);
                        logger.debug(
                            `📝 Registered ${sessionId} as active session for device ${deviceId} (during connection)`
                        );
                    }

                    // If stuck in connecting for more than 90 seconds, force reconnect
                    if (connectingDuration > 90000) {
                        logger.warn(
                            `⚠️  Stuck in connecting state for ${Math.round(connectingDuration / 1000)}s for ${sessionId} - forcing reconnect`,
                        );

                        // Mark as unstable and trigger reconnect
                        sessionData.set(sessionId, {
                            ...currentData,
                            status: "connection_unstable",
                            connectingStart: null, // Reset
                            isReconnecting: true,
                        });

                        // Trigger proactive reconnect after delay
                        setTimeout(async () => {
                            await proactiveReconnectIfDead(sessionId);
                        }, 3000);
                    } else {
                        // Update last active to show progress
                        sessionData.set(sessionId, {
                            ...currentData,
                            lastActive: Date.now(),
                        });
                    }
                } else if (connection === "open") {
                    const phoneNumber = socket.user?.id?.split(":")[0];

                    sessionData.set(sessionId, {
                        status: "connected",
                        phoneNumber,
                        qrCode: null,
                        lastActive: Date.now(),
                        reconnectionAttempts: 0, // Reset on successful connection
                        qrTimeoutAttempts: 0, // Reset QR timeout counter on successful connection
                        isReconnecting: false, // Reset reconnecting flag on successful connection
                        justPaired: false, // Reset pairing flag on successful connection
                        pairedAt: null, // Reset pairing timestamp
                        connectionQuality: {
                            successCount: 0,
                            failCount: 0,
                            lastCheck: Date.now(),
                        },
                    });

                    // ============================================
                    // RESET CIRCUIT BREAKER on successful connection
                    // ============================================
                    resetCircuitBreaker(sessionId);

                    // ============================================
                    // CLEANUP OLD SESSIONS for this device
                    // Ensures only one active session per device
                    // ============================================
                    await checkAndCleanupOldSessions(session_device_id, sessionId);

                    const data = sessionData.get(sessionId);
                    console.log("🔁 Session data:", data);
                    console.log("🔁 device id:", session_device_id);

                    if (data.status === "connected") {
                        await updateSessionStatus(sessionId, {
                            status: "connected",
                            phone_number: phoneNumber,
                        });
                        await updateDeviceStatus(
                            session_device_id,
                            "connected",
                        );
                        logger.info(
                            `✅ Session ${sessionId} connected successfully as ${phoneNumber}`,
                        );

                        // ============================================
                        // PROACTIVE HEALTH CHECK on connection
                        // ============================================
                        // Immediately test the connection with a lightweight operation
                        try {
                            await socket.fetchBlocklist();
                            logger.debug(
                                `✅ Initial health check passed for ${sessionId}`,
                            );
                        } catch (healthCheckError) {
                            logger.warn(
                                `⚠️  Initial health check failed for ${sessionId}:`,
                                healthCheckError.message,
                            );
                        }
                    }
                }
            } catch (connectionError) {
                // Critical: Catch any errors in connection handling to prevent crash
                logger.error({
                    msg: `❌ Error in connection handler for ${sessionId}`,
                    error: connectionError?.message || "Unknown error",
                    stack: connectionError?.stack,
                    sessionId,
                    connectionState: connection,
                });
                // Don't crash - let the error be logged and continue
            }
        });

        // Handle connection errors - CRITICAL to prevent crashes
        socket.ev.on("connection.errored", async (error) => {
            // Top-level try-catch to prevent unhandled rejections
            try {
                const session_device_id = extractSessionId(sessionId);

                logger.error({
                    msg: `⚠️ Connection error detected for ${sessionId}`,
                    error: error?.message || "Unknown error",
                    errorType: error?.constructor?.name,
                    stack: error?.stack,
                });

                // Check if this is a stream error
                if (
                    error?.message?.includes("Stream Errored") ||
                    error?.message?.includes("restart required")
                ) {
                    // ============================================
                    // CRITICAL: Stream error could be due to user action (device removed)
                    // or connection issue. Verify before deciding action.
                    // ============================================
                    logger.warn(
                        `🔄 Stream error detected for ${sessionId}, checking socket state...`,
                    );

                    try {
                        // First, verify if socket is actually alive
                        const isAlive = await verifySocketIsAlive(sessionId);

                        if (!isAlive) {
                            // ============================================
                            // Socket is dead - likely user removed device or permanent issue
                            // Mark as disconnected and DON'T reconnect
                            // ============================================
                            logger.warn(
                                `⚠️  Stream error with dead socket for ${sessionId} - marking as disconnected`,
                            );

                            await updateDeviceStatus(
                                session_device_id,
                                "disconnected",
                            ).catch((err) =>
                                logger.error(
                                    `Failed to update device status:`,
                                    err,
                                ),
                            );

                            await updateSessionStatus(sessionId, {
                                status: "disconnected",
                            }).catch((err) =>
                                logger.error(
                                    `Failed to update session status:`,
                                    err,
                                ),
                            );

                            // Clean up
                            sessions.delete(sessionId);
                            sessionData.delete(sessionId);
                            circuitBreakerState.delete(sessionId);

                            // CRITICAL FIX: Only delete from activeSessionsByDevice if THIS session
                            // is the registered active session
                            const registeredActiveSessionId = activeSessionsByDevice.get(session_device_id);
                            if (registeredActiveSessionId === sessionId) {
                                activeSessionsByDevice.delete(session_device_id);
                                logger.debug(
                                    `🗑️ Removed ${sessionId} from activeSessionsByDevice (was the registered active session)`
                                );
                            }

                            logger.info(
                                `✅ Session ${sessionId} marked as disconnected due to stream error with dead socket`,
                            );
                        } else {
                            // ============================================
                            // Socket is still alive - temporary connection issue
                            // Try to reconnect
                            // ============================================
                            logger.warn(
                                `🔄 Stream error but socket still alive for ${sessionId} - attempting graceful restart`,
                            );

                            sessionData.set(sessionId, {
                                ...sessionData.get(sessionId),
                                status: "reconnecting",
                                lastActive: Date.now(),
                            });

                            await updateDeviceStatus(
                                session_device_id,
                                "reconnecting",
                            ).catch((err) =>
                                logger.error(
                                    `Failed to update device status:`,
                                    err,
                                ),
                            );

                            // Close existing socket gracefully
                            if (socket.ws) {
                                socket.ws.close();
                            }

                            // Wait a bit before reconnecting
                            await delay(3000);

                            logger.info(
                                `🔄 Reconnecting ${sessionId} after stream error...`,
                            );
                        }
                    } catch (handleError) {
                        logger.error(
                            `❌ Error handling stream error for ${sessionId}:`,
                            handleError,
                        );
                    }
                }
            } catch (errorHandlerError) {
                // Catch any errors in the error handler itself (meta-error handling)
                logger.error({
                    msg: `❌ Error in error handler for ${sessionId}`,
                    error: errorHandlerError?.message || "Unknown error",
                    stack: errorHandlerError?.stack,
                    sessionId,
                });
                // Don't crash - just log
            }
        });

        // Handle incoming messages
        socket.ev.on("messages.upsert", async (messageUpdate) => {
            // Top-level try-catch to prevent unhandled rejections
            try {
                const { messages } = messageUpdate;
                const sessionDeviceId = extractSessionId(sessionId);

                for (const message of messages) {
                    if (message.key.fromMe || !message.message) continue;

                    // Check if auto-read is enabled for this device
                    const autoReadEnabled = await getDeviceAutoReadSetting(sessionDeviceId);
                    if (autoReadEnabled) {
                        await markMessageAsRead(socket, message);
                    }

                    const plainText = extractMessageText(message).trim();
                    const lowerText = plainText.toLowerCase();
                    const userId = message.key.remoteJid;

                    // ============================================
                    // LOG INBOUND MESSAGE TO DATABASE (async, non-blocking)
                    // This won't delay message processing - uses batch insert
                    // ============================================
                    logInboundMessage({
                        sessionId,
                        sessionDeviceId,
                        message,
                        plainText,
                    }).catch((err) => {
                        // Silently fail - logging should never crash message handler
                        logger.error(
                            { error: err.message },
                            "Failed to log inbound message"
                        );
                    });

                    // Store incoming message for quoting purposes
                    const messageId = message.key.id;
                    if (messageId) {
                        messageStore.set(messageId, {
                            key: message.key,
                            message: message.message,
                            sessionId,
                            timestamp: message.messageTimestamp || Date.now(),
                        });

                        // Clean up old messages from store
                        if (messageStore.size > 5000) {
                            const oldestKey = messageStore.keys().next().value;
                            messageStore.delete(oldestKey);
                        }
                    }

                    // Session for multi-step input
                    const session = await getUserSession(userId);
                    const menuConfig = await getMenuConfig(sessionDeviceId); // Fetch from DB
                    const menuItems = await getMenuItems(sessionDeviceId);
                    const menuText = buildMainMenuMessage(menuItems);

                    // Handle reset via "menu"
                    if (lowerText === "menu" && menuConfig?.is_menu_enabled) {
                        await clearUserSession(userId);

                        await socket.sendMessage(userId, {
                            text: menuConfig.main_menu_message,
                        });
                        continue;
                    }

                    // Step 1: Handle Follow-up Input State
                    if (session?.state) {
                        if (isSessionExpired(session.updated_at)) {
                            await clearUserSession(userId);
                            await socket.sendMessage(userId, {
                                text: "⏰ Sesi Anda telah berakhir. Ketik *menu* untuk mulai ulang.",
                            });

                            if (menuConfig?.is_menu_enabled) {
                                const menuItems = await getMenuItems(
                                    menuConfig.id,
                                );
                                const menuText =
                                    buildMainMenuMessage(menuItems);

                                await socket.sendMessage(userId, {
                                    text: menuText,
                                });
                            }

                            continue;
                        }

                        // Send acknowledgment to user
                        await socket.sendMessage(userId, {
                            text: `✅ Terima kasih! ${session.state}: *_${plainText}_*.\n\n Tim kami akan segera menyiapkan rekomendasi solusi otomasi yang paling cocok untuk Anda 💡.`,
                        });
                        // console.log("🔁 session:", session.data);

                        // Send to dynamic webhook for prouduction
                        if (session.data) {
                            // Send to webhook
                            await sendToWebhookAndRespond({
                                sessionId,
                                sessionDeviceId,
                                message,
                                plainText,
                                socket,
                                mainMenuMessage: null,
                                // mainMenuMessage:
                                // menuConfig?.main_menu_message || null,
                                webhookUrl: session.data, // Use the session's webhook URL
                                processingMode: "followup_state",
                                fallbackReason: "user_followup_input",
                                menuConfig,
                                userSessionData: session,
                                sessionData,
                                isSessionExpired,
                            });
                        }

                        await clearUserSession(userId);
                        continue;
                    }

                    // Step 2: Menu-Based Flow
                    if (menuConfig?.is_menu_enabled) {
                        console.log("🔁 Menu is enable :", menuConfig);
                        const menuItems = await getMenuItems(sessionDeviceId);
                        // console.log("🔁 Menu items:", menuItems);

                        const matchedItem = menuItems.find(
                            (item) =>
                                item.option_key === lowerText ||
                                lowerText.includes(item.label.toLowerCase()),
                        );
                        // console.log("🔁 Matched item:", matchedItem);

                        if (matchedItem) {
                            console.log("🔁 Matched item:", matchedItem);
                            if (matchedItem.requires_input) {
                                const messageLabel = matchedItem.label.replace(
                                    /\\n/g,
                                    "\n",
                                );

                                await socket.sendMessage(userId, {
                                    // text: `${matchedItem.label}`,
                                    text: messageLabel,
                                });

                                await setUserSession(
                                    userId,
                                    matchedItem.followup_state,
                                    matchedItem.webhook_url,
                                );
                            } else {
                                // Send to webhook
                                await sendToWebhookAndRespond({
                                    sessionId,
                                    sessionDeviceId,
                                    message,
                                    plainText,
                                    socket,
                                    mainMenuMessage: null,
                                    // mainMenuMessage:
                                    //     menuConfig?.main_menu_message || null,
                                    webhookUrl: matchedItem.webhook_url, // Use the session's webhook URL
                                    processingMode: "menu_flow",
                                    fallbackReason: "menu_item_matched",
                                    menuConfig,
                                    userSessionData: session,
                                    sessionData,
                                    isSessionExpired,
                                    matchedMenuItem: {
                                        id: matchedItem.id,
                                        label: matchedItem.label,
                                        optionKey: matchedItem.option_key,
                                    },
                                });

                                // await socket.sendMessage(userId, {
                                //     text: `✅ Kami teruskan ke layanan *${matchedItem.label}*`,
                                // });
                            }

                            continue; // prevent fallback
                        } else {
                            console.log("🔁 No match found, sending menu text");
                            await socket.sendMessage(userId, {
                                text: menuConfig.main_menu_message,
                            });
                        }
                    }

                    // Step 3: Fallback (Standard Mode or no match)
                    const fallbackUrl =
                        menuConfig?.default_webhook_url ||
                        "https://localhost/webhook";

                    // Validate webhook URL to prevent crashes
                    if (
                        !fallbackUrl ||
                        fallbackUrl === "https://localhost/webhook" ||
                        fallbackUrl === "http://localhost"
                    ) {
                        logger.warn({
                            msg: `⚠️ Skipping fallback webhook - invalid or default URL`,
                            fallbackUrl,
                            sessionId,
                            userId,
                        });
                        // Don't crash - just skip the webhook and continue
                        continue;
                    }

                    // Get user session data for additional context
                    const userSessionData = await getUserSession(userId);

                    // Build complete enhanced webhook payload using helper
                    const webhookPayload = await buildEnhancedWebhookPayload({
                        sessionId,
                        sessionDeviceId,
                        socket,
                        message,
                        plainText,
                        userId,
                        sessionData,
                        menuConfig,
                        processingMode: "standard_fallback",
                        fallbackReason: "no_menu_match_or_no_session",
                        userSessionData,
                        isSessionExpired,
                    });

                    // Update webhook URL in payload
                    webhookPayload.webhookUrl = fallbackUrl;

                    // Send to fallback webhook with ENHANCED error handling
                    try {
                        await axios.post(fallbackUrl, webhookPayload, {
                            headers: {
                                "Content-Type": "application/json",
                                "User-Agent": "Baileys-Flowkirim/1.0",
                            },
                            timeout: 30000, // 30 second timeout
                            validateStatus: (status) => status < 500, // Don't throw for 4xx
                        });

                        logger.info(
                            `Fallback webhook sent successfully: ${fallbackUrl}`,
                            {
                                messageType: webhookPayload.messageType,
                                userId,
                                sessionId,
                            },
                        );
                    } catch (webhookError) {
                        const errorCode = webhookError?.code || "UNKNOWN";
                        const isNetworkError =
                            errorCode === "ECONNREFUSED" ||
                            errorCode === "ENOTFOUND" ||
                            errorCode === "ETIMEDOUT" ||
                            errorCode === "ECONNRESET";

                        logger.error({
                            msg: `❌ Failed to send to fallback webhook`,
                            fallbackUrl,
                            error: webhookError?.message || "Unknown error",
                            code: errorCode,
                            isNetworkError,
                            response: webhookError?.response?.data || null,
                            status: webhookError?.response?.status || null,
                            userId,
                            sessionId,
                        });

                        // Don't crash the server - just log the error and continue
                        // The message processing will continue normally
                    }

                    // await socket.sendMessage(userId, {
                    //     text: "✅ Pesan Anda telah kami terima. Tim kami akan segera merespon.",
                    // });
                }
            } catch (messageError) {
                // Critical: Catch any errors in message processing to prevent crash
                logger.error({
                    msg: `❌ Error processing message for ${sessionId}`,
                    error: messageError?.message || "Unknown error",
                    stack: messageError?.stack,
                    sessionId,
                });
                // Don't crash - continue processing other messages
            }
        });

        // Handle credential errors
        socket.ev.on("creds.error", async (error) => {
            logger.error({
                msg: `❌ Credentials error for ${sessionId}`,
                error: error?.message || "Unknown error",
                stack: error?.stack,
            });

            // Credential errors are usually fatal - need to re-authenticate
            const session_device_id = extractSessionId(sessionId);
            await updateDeviceStatus(
                session_device_id,
                "authentication_failed",
            ).catch((err) =>
                logger.error(`Failed to update device status:`, err),
            );

            logger.warn(
                `📱 Session ${sessionId} requires re-authentication (scan QR again)`,
            );
        });

        // Handle any other socket errors
        socket.ev.on("error", async (error) => {
            logger.error({
                msg: `⚠️ Socket error for ${sessionId}`,
                error: error?.message || "Unknown error",
                errorType: error?.constructor?.name,
            });

            // Log but don't crash - let connection.update handle reconnect
        });

        // Handle group metadata updates - update cache when group info changes
        socket.ev.on("groups.update", async (events) => {
            try {
                for (const event of events) {
                    if (event?.id) {
                        logger.debug(`Group metadata update for ${event.id}`);
                        const metadata = await socket.groupMetadata(event.id);
                        groupMetadataCache.set(event.id, metadata);
                        logger.debug(
                            `Updated group metadata cache for ${event.id}`,
                        );
                    }
                }
            } catch (error) {
                logger.error(`Error updating group metadata:`, error);
            }
        });

        // Handle group participant updates - update cache when participants change
        socket.ev.on("group-participants.update", async (event) => {
            try {
                if (event?.id) {
                    logger.debug(`Group participants update for ${event.id}`);
                    const metadata = await socket.groupMetadata(event.id);
                    groupMetadataCache.set(event.id, metadata);
                    logger.debug(
                        `Updated group metadata cache for ${event.id} (participants changed)`,
                    );
                }
            } catch (error) {
                logger.error(
                    `Error updating group metadata for participants:`,
                    error,
                );
            }
        });

        return socket;
    } catch (error) {
        logger.error(`Error creating WhatsApp socket for ${sessionId}:`, error);

        // If there's an error during socket creation, clean up session files
        try {
            await fs.rm(getSessionDir(sessionId), {
                recursive: true,
                force: true,
            });
        } catch (cleanupError) {
            logger.error(
                `Error cleaning up session files after socket creation error:`,
                cleanupError,
            );
        }

        throw error;
    }
};

// ============================================
// HELPER: Recover phoneNumber from credentials file
// This prevents sessions from being marked as "never connected" after server restart
// ============================================
const recoverPhoneNumberFromCreds = async (sessionId) => {
    try {
        // ============================================
        // ENHANCED: Use credentialsManager with validation & recovery
        // First validate and recover credentials if corrupted
        // ============================================

        // Step 1: Validate and try to recover corrupted credentials
        const validation = await validateAndRecoverCredentials(sessionId);

        if (!validation.valid) {
            logger.debug(
                `⚠️  No valid credentials for ${sessionId}: ${validation.error}`,
            );
            return null;
        }

        if (validation.recovered) {
            logger.info(
                `✅ Auto-recovered corrupted credentials for ${sessionId}`,
            );
        }

        // Step 2: Extract phoneNumber using credentialsManager
        const phoneNumber = await extractPhoneNumberFromCreds(sessionId);

        if (phoneNumber) {
            logger.info(
                `✅ Recovered phoneNumber "${phoneNumber}" from credentials for ${sessionId}`,
            );
            return phoneNumber;
        } else {
            logger.debug(
                `⚠️  Could not extract phoneNumber from credentials for ${sessionId}`,
            );
            return null;
        }
    } catch (error) {
        logger.debug(
            `⚠️  No credentials file found for ${sessionId}: ${error.message}`,
        );
        return null;
    }
};

// Load existing sessions at startup with ENHANCED error handling
const loadExistingSessions = async () => {
    const sessionsDir = getSessionsBaseDir();
    try {
        const sessionFolders = await fs.readdir(sessionsDir);

        if (sessionFolders.length === 0) {
            logger.info("No existing sessions to load");
            return;
        }

        logger.info(
            `Found ${sessionFolders.length} existing session folder(s), analyzing...`,
        );

        // ============================================
        // CRITICAL FIX: Only load the LATEST session for each device
        // Session ID format: {deviceId}_{timestamp}
        // We group by deviceId and only keep the one with the highest timestamp
        // ============================================
        const sessionsByDevice = new Map();

        for (const sessionId of sessionFolders) {
            const deviceId = extractSessionId(sessionId);

            // Extract timestamp from sessionId (format: {deviceId}_{timestamp})
            const timestampMatch = sessionId.match(/_(\d+)$/);
            const timestamp = timestampMatch ? parseInt(timestampMatch[1]) : 0;

            if (!sessionsByDevice.has(deviceId)) {
                sessionsByDevice.set(deviceId, { sessionId, timestamp });
            } else {
                const existing = sessionsByDevice.get(deviceId);
                if (timestamp > existing.timestamp) {
                    // Found a newer session, mark old one for deletion
                    logger.info(
                        `🔄 Found newer session for ${deviceId}: ${sessionId} (timestamp: ${timestamp}) vs ${existing.sessionId} (timestamp: ${existing.timestamp})`,
                    );

                    // Delete old session folder
                    try {
                        const oldSessionDir = getSessionDir(existing.sessionId);
                        await fs.rm(oldSessionDir, { recursive: true, force: true });
                        logger.info(
                            `🗑️ Deleted old session folder: ${existing.sessionId}`,
                        );
                    } catch (deleteError) {
                        logger.debug(
                            `⚠️ Could not delete old session folder: ${deleteError.message}`,
                        );
                    }

                    sessionsByDevice.set(deviceId, { sessionId, timestamp });
                } else {
                    // Current session is older, delete it
                    logger.info(
                        `🔄 Found newer session for ${deviceId}: ${existing.sessionId}, skipping old: ${sessionId}`,
                    );

                    try {
                        const oldSessionDir = getSessionDir(sessionId);
                        await fs.rm(oldSessionDir, { recursive: true, force: true });
                        logger.info(
                            `🗑️ Deleted old session folder: ${sessionId}`,
                        );
                    } catch (deleteError) {
                        logger.debug(
                            `⚠️ Could not delete old session folder: ${deleteError.message}`,
                        );
                    }
                }
            }
        }

        // Get the latest sessions to load
        const sessionsToLoad = Array.from(sessionsByDevice.values()).map(v => v.sessionId);

        logger.info(
            `After deduplication: Loading ${sessionsToLoad.length} session(s) (skipped ${sessionFolders.length - sessionsToLoad.length} old session(s))`,
        );

        // Load sessions sequentially to avoid overwhelming the system
        for (let i = 0; i < sessionsToLoad.length; i++) {
            const sessionId = sessionsToLoad[i];
            try {
                logger.info(
                    `[${i + 1}/${sessionsToLoad.length}] Loading existing session: ${sessionId}`,
                );

                // ============================================
                // CRITICAL FIX: Validate and recover credentials BEFORE loading
                // This prevents loading corrupted credentials that cause 401 errors
                // ============================================
                logger.info(`🔍 Validating credentials for ${sessionId}...`);
                const credentialsCheck = await validateAndRecoverCredentials(sessionId);

                if (!credentialsCheck.valid) {
                    logger.error(
                        `❌ Credentials for ${sessionId} are invalid and no backup available - skipping this session`,
                    );
                    // Delete corrupted session folder
                    try {
                        const corruptedDir = getSessionDir(sessionId);
                        await fs.rm(corruptedDir, { recursive: true, force: true });
                        logger.info(
                            `🗑️ Deleted corrupted session folder: ${sessionId}`,
                        );
                    } catch (deleteError) {
                        logger.debug(
                            `⚠️ Could not delete corrupted session folder: ${deleteError.message}`,
                        );
                    }
                    continue; // Skip this session
                }

                if (credentialsCheck.recovered) {
                    logger.warn(
                        `⚠️ Credentials for ${sessionId} were corrupted but recovered from backup`,
                    );
                } else {
                    logger.info(`✅ Credentials for ${sessionId} are valid`);
                }

                // Stagger startup to avoid concurrent connection issues
                // Increased delay to give each session more time to stabilize
                if (i > 0) {
                    await delay(5000); // 5 second delay between each session (increased from 2s)
                    logger.debug(
                        `⏱️  Stagger delay completed, starting next session...`,
                    );
                }

                const socket = await createWhatsAppSocket(sessionId);
                sessions.set(sessionId, socket);

                // ============================================
                // CRITICAL FIX: Try to recover phoneNumber from credentials file
                // This prevents sessions from being marked as "never connected" after restart
                // ============================================
                const recoveredPhoneNumber = await recoverPhoneNumberFromCreds(sessionId);

                sessionData.set(sessionId, {
                    status: "connecting",
                    phoneNumber: recoveredPhoneNumber || undefined, // Set recovered phoneNumber if available
                    lastActive: Date.now(),
                    reconnectionAttempts: 0,
                    qrTimeoutAttempts: 0,
                    connectionQuality: {
                        successCount: 0,
                        failCount: 0,
                        lastCheck: Date.now(),
                    },
                });

                if (recoveredPhoneNumber) {
                    logger.info(
                        `📱 Session ${sessionId} has phoneNumber: ${recoveredPhoneNumber}`,
                    );
                }

                logger.info(`✅ Session ${sessionId} loaded successfully`);
            } catch (error) {
                logger.error(`❌ Error loading session ${sessionId}:`, error);

                // Mark failed sessions but don't crash
                sessionData.set(sessionId, {
                    status: "failed",
                    lastActive: Date.now(),
                    reconnectionAttempts: 0,
                    error: error.message,
                });
            }
        }

        logger.info(
            `✅ Session loading complete: ${sessions.size}/${sessionsToLoad.length} active`,
        );
    } catch (error) {
        logger.error("Error loading existing sessions:", error);
    }
};

// ============================================
// STARTUP: Verify device status vs actual sessions
// Ensures database status matches reality after server restart
// ============================================
const verifyDeviceStatusOnStartup = async () => {
    try {
        logger.info(
            "🔍 Verifying device status against active sessions...",
        );

        // Get all devices with status "connected" and source "flowkirim" from database
        let connectedDevices = [];
        try {
            const { rows } = await query(
                `SELECT id, session_device_id, status FROM devices WHERE status = $1 AND source = $2`,
                ["connected", "flowkirim"]
            );
            connectedDevices = rows;
        } catch (error) {
            logger.error("Error fetching connected devices:", error);
            return;
        }

        if (!connectedDevices || connectedDevices.length === 0) {
            logger.info("✅ No connected devices to verify");
            return;
        }

        logger.info(
            `Found ${connectedDevices.length} devices with status "connected" in database`,
        );

        let updatedCount = 0;
        let verifiedCount = 0;
        let skippedCount = 0;

        for (const device of connectedDevices) {
            const deviceId = device.session_device_id;

            // Check if there's an active or connecting session for this device
            let hasActiveSession = false;
            let hasConnectingSession = false;
            let activeSessionId = null;
            let connectingSessionId = null;

            for (const [sessionId, socket] of sessions.entries()) {
                const sessionDeviceId = extractSessionId(sessionId);

                if (sessionDeviceId === deviceId) {
                    const session = sessionData.get(sessionId);

                    if (session) {
                        if (session.status === "connected") {
                            hasActiveSession = true;
                            activeSessionId = sessionId;
                            break; // Found connected session, no need to check further
                        } else if (session.status === "connecting") {
                            hasConnectingSession = true;
                            connectingSessionId = sessionId;
                            // Don't break, keep checking for other sessions that might be connected
                        }
                    }
                }
            }

            if (hasActiveSession) {
                // Device is actually connected
                logger.debug(
                    `✅ Device ${deviceId} is verified connected (session: ${activeSessionId})`,
                );
                verifiedCount++;
            } else if (hasConnectingSession) {
                // Device has a session that's still connecting - SKIP verification
                // It might connect soon, so we don't want to mark it as disconnected
                logger.info(
                    `⏳ Device ${deviceId} has a connecting session (${connectingSessionId}) - skipping verification for now`,
                );
                skippedCount++;
            } else {
                // Device has no active or connecting session but status is "connected" - update to "disconnected"
                logger.warn(
                    `⚠️  Device ${deviceId} has status "connected" but no active/connecting session found - updating to "disconnected"`,
                );

                try {
                    // Update devices table
                    await query(
                        `UPDATE devices SET status = $1 WHERE session_device_id = $2`,
                        ["disconnected", deviceId]
                    );

                    // Update whatsapp_sessions table
                    await query(
                        `UPDATE whatsapp_sessions SET status = $1 WHERE session_device_id = $2`,
                        ["disconnected", deviceId]
                    );

                    logger.info(
                        `✅ Updated device ${deviceId} status to "disconnected"`,
                    );
                    updatedCount++;
                } catch (updateError) {
                    logger.error(
                        `❌ Error updating device ${deviceId} status:`,
                        updateError,
                    );
                }
            }
        }

        logger.info(
            `🔍 Device verification complete: ${verifiedCount} verified connected, ${skippedCount} skipped (connecting), ${updatedCount} updated to disconnected`,
        );
    } catch (error) {
        logger.error("Error verifying device status on startup:", error);
    }
};

// Clean up inactive sessions
const cleanupInactiveSessions = async () => {
    const inactiveThreshold = 24 * 60 * 60 * 1000; // 24 hours
    setInterval(
        async () => {
            for (const [sessionId, data] of sessionData) {
                if (
                    data.lastActive &&
                    Date.now() - data.lastActive > inactiveThreshold
                ) {
                    logger.info(`Cleaning up inactive session: ${sessionId}`);
                    const session_device_id = extractSessionId(sessionId);

                    // CRITICAL: Check if socket is actually alive before cleaning up
                    const isAlive = await verifySocketIsAlive(sessionId);

                    if (isAlive) {
                        logger.warn(
                            `⚠️  Session ${sessionId} is inactive but socket is still ALIVE (verified via ping) - skipping cleanup`,
                        );
                        // Update lastActive instead of cleaning up
                        sessionData.set(sessionId, {
                            ...data,
                            lastActive: Date.now(),
                        });
                        continue;
                    }

                    // Update device status before cleanup
                    await updateDeviceStatus(
                        session_device_id,
                        "disconnected",
                    ).catch((err) =>
                        logger.error(
                            `Failed to update device status during cleanup:`,
                            err,
                        ),
                    );

                    // Update session status before cleanup
                    await updateSessionStatus(sessionId, {
                        status: "disconnected",
                    }).catch((err) =>
                        logger.error(
                            `Failed to update session status during cleanup:`,
                            err,
                        ),
                    );

                    const socket = sessions.get(sessionId);
                    if (socket) {
                        await socket.logout();
                        sessions.delete(sessionId);
                    }
                    sessionData.delete(sessionId);
                    await fs.rm(getSessionDir(sessionId), {
                        recursive: true,
                        force: true,
                    });
                }
            }
        },
        60 * 60 * 1000,
    ); // Check every hour
};

// NEW: Enhanced connection health monitoring with proactive ping
const monitorConnectionHealth = async () => {
    // ============================================
    // MORE AGGRESSIVE HEALTH CHECK SETTINGS
    // ============================================
    const healthCheckInterval = 60 * 1000; // Every 1 MINUTE (was 3 minutes) - MORE FREQUENT
    const staleThreshold = 3 * 60 * 1000; // 3 minutes without activity (was 8) - EARLIER DETECTION
    const warningThreshold = 90 * 1000; // 90 seconds - EARLIER WARNING (was 5 minutes)

    // Track connection quality for each session
    const connectionQuality = new Map(); // sessionId -> { successCount, failCount, lastCheck }

    setInterval(async () => {
        logger.debug("Running proactive connection health check...");

        for (const [sessionId, data] of sessionData) {
            if (data.status !== "connected") continue;

            const socket = sessions.get(sessionId);
            if (!socket) {
                logger.warn(
                    `Socket not found for connected session ${sessionId}, attempting to recover...`,
                );
                await handleMissingSession(sessionId);
                continue;
            }

            const timeSinceLastActive = Date.now() - (data.lastActive || 0);

            // Track connection quality
            const quality = connectionQuality.get(sessionId) || {
                successCount: 0,
                failCount: 0,
                lastCheck: Date.now(),
            };
            connectionQuality.set(sessionId, quality);

            // ============================================
            // REGULAR PING: Even if connection is healthy
            // ============================================
            if (timeSinceLastActive < warningThreshold) {
                // Connection is healthy, but still ping occasionally
                try {
                    if (socket.user && socket.user.id && Math.random() < 0.3) {
                        // 30% chance each check
                        // Lightweight ping
                        await socket.fetchBlocklist();

                        quality.successCount++;
                        logger.debug(
                            `💓 Routine ping successful for ${sessionId} (${Math.round(timeSinceLastActive / 1000)}s since last activity)`,
                        );
                    }
                } catch (pingError) {
                    quality.failCount++;
                    recordFailure(sessionId);
                    logger.warn(
                        `⚠️  Routine ping failed for ${sessionId}:`,
                        pingError.message,
                    );
                }
            }

            // Warning level - no activity for 90 seconds
            if (
                timeSinceLastActive > warningThreshold &&
                timeSinceLastActive <= staleThreshold
            ) {
                logger.warn(
                    `⚠️  Session ${sessionId} warning: no activity for ${Math.round(timeSinceLastActive / 1000)}s`,
                );

                // Try proactive ping to wake up connection
                try {
                    if (socket.user && socket.user.id) {
                        logger.info(
                            `🔔 Proactive ping for session ${sessionId} to prevent stale connection`,
                        );

                        // Send a lightweight query to verify connection is alive
                        await socket.fetchBlocklist();

                        // Update lastActive timestamp
                        sessionData.set(sessionId, {
                            ...data,
                            lastActive: Date.now(),
                        });

                        quality.successCount++;
                        recordSuccess(sessionId);
                        logger.info(
                            `✅ Proactive ping successful for ${sessionId} (quality: ${quality.successCount}/${quality.successCount + quality.failCount})`,
                        );
                    }
                } catch (pingError) {
                    quality.failCount++;
                    recordFailure(sessionId);
                    logger.warn(
                        `⚠️  Proactive ping failed for ${sessionId}:`,
                        pingError.message,
                    );

                    // If ping fails multiple times, initiate recovery early
                    if (quality.failCount >= 3) {
                        logger.warn(
                            `🔧 Multiple ping failures for ${sessionId}, considering early recovery`,
                        );
                    }
                }
            }

            // Critical level - no activity for 3 minutes, attempt recovery
            if (timeSinceLastActive > staleThreshold) {
                logger.error(
                    `🚨 Session ${sessionId} appears stale (no activity for ${Math.round(timeSinceLastActive / 1000)}s)`,
                );

                const successRate =
                    quality.successCount /
                    (quality.successCount + quality.failCount || 1);
                logger.info(
                    `📊 Connection quality for ${sessionId}: ${Math.round(successRate * 100)}% success rate (${quality.successCount} success, ${quality.failCount} fail)`,
                );

                const session_device_id = extractSessionId(sessionId);

                // Check circuit breaker before attempting recovery
                if (!canAttemptOperation(sessionId)) {
                    logger.warn(
                        `⚠️  Circuit breaker OPEN for ${sessionId}, skipping automatic recovery`,
                    );
                    continue;
                }

                // Try to recover the session
                try {
                    logger.info(
                        `🔧 Attempting to recover stale session ${sessionId}...`,
                    );

                    // CRITICAL: Close old socket first to prevent resource leaks
                    const oldSocket = sessions.get(sessionId);
                    if (oldSocket) {
                        try {
                            if (oldSocket.ws) {
                                oldSocket.ws.close();
                            }
                            logger.debug(`Closed old socket for ${sessionId}`);
                        } catch (closeError) {
                            logger.warn(
                                `Error closing old socket:`,
                                closeError,
                            );
                        }
                    }

                    await updateSessionStatus(sessionId, {
                        status: "connecting",
                    });

                    // Reset connection quality tracking
                    connectionQuality.delete(sessionId);
                    resetCircuitBreaker(sessionId);

                    const newSocket = await createWhatsAppSocket(sessionId, 0);
                    sessions.set(sessionId, newSocket);

                    logger.info(
                        `✅ Successfully recovered stale session ${sessionId}`,
                    );
                } catch (recoveryError) {
                    recordFailure(sessionId);
                    logger.error(
                        `❌ Failed to recover session ${sessionId}:`,
                        recoveryError,
                    );

                    // Mark as disconnected if recovery fails AND socket is truly dead
                    const isAlive = await verifySocketIsAlive(sessionId);
                    if (!isAlive) {
                        await updateDeviceStatus(
                            session_device_id,
                            "disconnected",
                        ).catch((err) =>
                            logger.error(
                                `Failed to update device status:`,
                                err,
                            ),
                        );

                        await updateSessionStatus(sessionId, {
                            status: "disconnected",
                        }).catch((err) =>
                            logger.error(
                                `Failed to update session status:`,
                                err,
                            ),
                        );
                    } else {
                        logger.warn(
                            `⚠️  Recovery failed but socket still ALIVE (verified via ping) for ${sessionId} - keeping session alive`,
                        );
                    }
                }
            }
        }

        logger.debug("✅ Proactive health check completed");
    }, healthCheckInterval);

    logger.info(
        `🔍 Enhanced connection health monitoring started (interval: ${healthCheckInterval}ms, stale threshold: ${staleThreshold}ms)`,
    );

    // ============================================
    // AGGRESSIVE PERIODIC KEEPALIVE
    // ============================================
    setInterval(async () => {
        logger.debug("💓 Sending periodic keepalive to all sessions...");

        for (const [sessionId, data] of sessionData) {
            if (data.status !== "connected") continue;

            const socket = sessions.get(sessionId);
            if (!socket) continue;

            try {
                // Update lastActive to show we're alive
                sessionData.set(sessionId, {
                    ...data,
                    lastActive: Date.now(),
                });

                // Also send a lightweight ping
                if (socket.ws && socket.ws.readyState === 1) {
                    // Send a very lightweight operation
                    socket.ev.emit("keep-alive");
                }
            } catch (error) {
                logger.warn(
                    `Keepalive failed for ${sessionId}:`,
                    error.message,
                );
            }
        }
    }, 30000); // Every 30 SECONDS - MORE AGGRESSIVE (was 60s)

    logger.info(`💓 Aggressive periodic keepalive started (interval: 30000ms)`);
};

// API Routes

// ENHANCED Health check with detailed connection status
app.get("/health", (req, res) => {
    const sessionDetails = [];
    for (const [sessionId, data] of sessionData) {
        const socket = sessions.get(sessionId);

        // Get circuit breaker status
        const breaker = circuitBreakerState.get(sessionId);
        const breakerStatus = breaker
            ? {
                  state: breaker.state,
                  failures: breaker.failures,
                  cooldownUntil: breaker.cooldownUntil
                      ? new Date(breaker.cooldownUntil).toISOString()
                      : null,
                  isInCooldown: breaker.state === CIRCUIT_STATE.OPEN,
              }
            : null;

        sessionDetails.push({
            sessionId,
            status: data.status,
            phoneNumber: data.phoneNumber,
            lastActive: data.lastActive
                ? new Date(data.lastActive).toISOString()
                : null,
            lastActiveSeconds: data.lastActive
                ? Math.round((Date.now() - data.lastActive) / 1000)
                : null,
            reconnectionAttempts: data.reconnectionAttempts || 0,
            socketConnected: !!socket,
            socketReadyState: socket?.ws?.readyState, // WebSocket ready state
            socketUser: socket?.user?.id?.split(":")[0] || null,
            circuitBreaker: breakerStatus,
        });
    }

    res.json({
        success: true,
        status: process.isShuttingDown ? "SHUTTING_DOWN" : "OK",
        timestamp: new Date().toISOString(),
        uptime: Math.round(process.uptime()),
        uptimeFormatted: formatUptime(process.uptime()),
        node_version: process.version,
        platform: process.platform,
        memory: {
            used:
                Math.round(process.memoryUsage().heapUsed / 1024 / 1024) +
                " MB",
            total:
                Math.round(process.memoryUsage().heapTotal / 1024 / 1024) +
                " MB",
            rss: Math.round(process.memoryUsage().rss / 1024 / 1024) + " MB",
        },
        webhook_url: WEBHOOK_URL,
        bulk_jobs: bulkMessageJobs.size,
        scheduled_messages: scheduledMessages.size,
        message_store_size: messageStore.size,
        circuitBreakersActive: Array.from(circuitBreakerState.entries())
            .filter(([_, b]) => b.state === CIRCUIT_STATE.OPEN)
            .map(([id, _]) => id),
        sessions: {
            total: sessions.size,
            connected: sessionDetails.filter((s) => s.status === "connected")
                .length,
            connecting: sessionDetails.filter((s) => s.status === "connecting")
                .length,
            failed: sessionDetails.filter(
                (s) => s.status === "failed" || s.status === "disconnected",
            ).length,
            details: sessionDetails,
        },
    });
});

// Helper function to format uptime
function formatUptime(seconds) {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

    return parts.join(" ");
}

// Debug endpoint to view message store
app.get("/api/debug/messages", (req, res) => {
    const { limit = 20, sessionId } = req.query;

    let messages = Array.from(messageStore.entries());

    if (sessionId) {
        messages = messages.filter(([_, msg]) => msg.sessionId === sessionId);
    }

    messages = messages.slice(0, parseInt(limit));

    res.json({
        success: true,
        total: messageStore.size,
        filtered: messages.length,
        messages: messages.map(([id, msg]) => ({
            id,
            sessionId: msg.sessionId,
            key: {
                remoteJid: msg.key?.remoteJid,
                fromMe: msg.key?.fromMe,
                id: msg.key?.id,
            },
            messageType: msg.message ? Object.keys(msg.message) : [],
            messageText: msg.message?.text || msg.message?.conversation || null,
            timestamp: new Date(msg.timestamp).toISOString(),
            age: Math.floor((Date.now() - msg.timestamp) / 1000) + "s ago",
        })),
    });
});

// ============================================
// NEW: Circuit breaker management endpoints
// ============================================

// Get circuit breaker status for all sessions
app.get("/api/debug/circuit-breakers", (req, res) => {
    try {
        const breakers = [];

        for (const [sessionId, breaker] of circuitBreakerState.entries()) {
            const session = sessionData.get(sessionId);

            breakers.push({
                sessionId,
                state: breaker.state,
                failures: breaker.failures,
                lastFailureTime: breaker.lastFailureTime
                    ? new Date(breaker.lastFailureTime).toISOString()
                    : null,
                cooldownUntil: breaker.cooldownUntil
                    ? new Date(breaker.cooldownUntil).toISOString()
                    : null,
                isInCooldown: breaker.state === CIRCUIT_STATE.OPEN,
                remainingCooldownMs: breaker.cooldownUntil
                    ? Math.max(0, breaker.cooldownUntil - Date.now())
                    : 0,
                sessionStatus: session?.status || "unknown",
            });
        }

        res.json({
            success: true,
            total: breakers.length,
            open: breakers.filter((b) => b.state === CIRCUIT_STATE.OPEN).length,
            halfOpen: breakers.filter(
                (b) => b.state === CIRCUIT_STATE.HALF_OPEN,
            ).length,
            closed: breakers.filter((b) => b.state === CIRCUIT_STATE.CLOSED)
                .length,
            breakers,
        });
    } catch (error) {
        logger.error("Error getting circuit breaker status:", error);
        res.status(500).json({
            success: false,
            message: "Error getting circuit breaker status",
            error: error.message,
        });
    }
});

// Reset circuit breaker for a session
app.post("/api/debug/circuit-breakers/:sessionId/reset", (req, res) => {
    try {
        const { sessionId } = req.params;

        if (!circuitBreakerState.has(sessionId)) {
            return res.status(404).json({
                success: false,
                message: "No circuit breaker found for this session",
            });
        }

        resetCircuitBreaker(sessionId);

        res.json({
            success: true,
            message: `Circuit breaker reset for session ${sessionId}`,
        });
    } catch (error) {
        logger.error("Error resetting circuit breaker:", error);
        res.status(500).json({
            success: false,
            message: "Error resetting circuit breaker",
            error: error.message,
        });
    }
});

// Create a new session
app.post("/api/sessions", async (req, res) => {
    try {
        if (!req.body) {
            logger.warn("Request body is missing");
            return res.status(400).json({
                success: false,
                message: "Request body is missing",
            });
        }

        const { sessionId } = req.body;

        if (!sessionId) {
            logger.warn("sessionId is required");
            return res.status(400).json({
                success: false,
                message: "sessionId is required",
            });
        }

        if (sessions.has(sessionId)) {
            logger.warn(`Session ${sessionId} already exists`);
            return res.status(409).json({
                success: false,
                message: "Session already exists",
            });
        }

        sessionData.set(sessionId, {
            status: "connecting",
            lastActive: Date.now(),
            reconnectionAttempts: 0,
            qrTimeoutAttempts: 0,
        });
        logger.debug(`Starting session creation: ${sessionId}`);

        const socket = await createWhatsAppSocket(sessionId);
        // console.error("❌ socket:", socket);
        sessions.set(sessionId, socket);

        await delay(2000);

        const data = sessionData.get(sessionId) || {};
        console.log("❌ DATA:", data);
        // logger.info("❌ Session created successfully YA", data);

        res.json({
            success: true,
            sessionId,
            status: data.status,
            qrCode: data.qrCode,
        });
    } catch (error) {
        logger.error("Error creating session:", {
            error: error.message,
            stack: error.stack,
        });
        res.status(500).json({
            success: false,
            message: "Error creating session",
            error: error.message || "Unknown error",
        });
    }
});

// Get session status
app.get("/api/sessions/:sessionId/status", (req, res) => {
    const { sessionId } = req.params;

    const session = sessions.get(sessionId);
    const data = sessionData.get(sessionId) || { status: "not_found" };

    if (!session && data.status === "not_found") {
        logger.warn(`Session ${sessionId} not found`);
        return res.status(404).json({
            success: false,
            message: "Session not found",
        });
    }

    res.json({
        success: true,
        sessionId,
        status: data.status,
        phoneNumber: data.phoneNumber,
        qrCode: data.qrCode,
        lastActive: data.lastActive,
    });
});

// Send text message
app.post("/api/messages/text", async (req, res) => {
    try {
        const { sessionId, to, message, mentions } = req.body;

        // Support both camelCase and snake_case for quoted_message_id
        const quotedMessageId =
            req.body.quotedMessageId || req.body.quoted_message_id || null;

        if (!sessionId || !to || !message) {
            logger.warn("Request body for text message is missing", {
                body: req.body,
            });
            return res.status(400).json({
                success: false,
                message: "sessionId, to, and message are required",
            });
        }

        const formattedNumber = validateWhatsAppNumber(to);
        if (!formattedNumber) {
            logger.warn("Invalid number", { to });
            return res.status(400).json({
                success: false,
                message: "Invalid number",
            });
        }

        const socket = sessions.get(sessionId);
        if (!socket) {
            await handleMissingSession(sessionId);
            return res.status(404).json({
                success: false,
                message: "Session not found",
            });
        }

        const data = sessionData.get(sessionId);
        if (data?.status !== "connected") {
            await handleDisconnectedSession(
                sessionId,
                data?.status || "unknown",
            );
            return res.status(400).json({
                success: false,
                message: "Session not connected",
            });
        }

        // Parse mentions from text if mentions array is not provided
        const parsedMentions = mentions || parseMentions(message);

        // Build message options for Baileys
        const options = {};

        // Add quoted message if provided - retrieve actual message from store
        if (quotedMessageId) {
            try {
                const storedMessage = messageStore.get(quotedMessageId);

                if (storedMessage) {
                    // Transform message to proper quote format
                    let quotedMsg = storedMessage.message;

                    // Handle different message types for quotes
                    if (quotedMsg.text) {
                        quotedMsg = { conversation: quotedMsg.text };
                    } else if (quotedMsg.image) {
                        quotedMsg = { imageMessage: quotedMsg.image };
                    } else if (quotedMsg.video) {
                        quotedMsg = { videoMessage: quotedMsg.video };
                    } else if (quotedMsg.document) {
                        quotedMsg = { documentMessage: quotedMsg.document };
                    }

                    // Use the actual stored message for proper quote preview
                    options.quoted = {
                        key: storedMessage.key,
                        message: quotedMsg,
                    };
                } else {
                    // Fallback to stub if message not found in store
                    options.quoted = {
                        key: {
                            remoteJid: formattedNumber,
                            id: quotedMessageId,
                            fromMe: false,
                            participant: formattedNumber.includes("@g.us")
                                ? formattedNumber
                                : undefined,
                        },
                        message: { conversation: "‎" },
                    };
                }
            } catch (e) {
                logger.error("Error creating quote:", { error: e.message });
            }
        }

        // Add mentions if provided
        if (parsedMentions.length > 0) {
            options.mentionedJid = parsedMentions;
        }

        // Send message with options - use sendMessage utility to store the message
        const messageContent = { text: message };
        const result = await sendMessage(
            socket,
            formattedNumber,
            messageContent,
            sessionId,
            Object.keys(options).length > 0 ? options : undefined,
        );

        res.json({
            success: true,
            messageId: result.key.id,
            timestamp: result.messageTimestamp,
            quoted: quotedMessageId || null,
            mentions: parsedMentions,
        });
    } catch (error) {
        logger.error("Error sending text message:", {
            error: error.message,
            stack: error.stack,
        });
        res.status(500).json({
            success: false,
            message: "Error sending text message",
            error: error.message || "Unknown error",
        });
    }
});

// Send text message to group
app.post("/api/messages/group/text", async (req, res) => {
    try {
        const { sessionId, groupId, message, mentions } = req.body;

        // Support both camelCase and snake_case for quoted_message_id
        const quotedMessageId =
            req.body.quotedMessageId || req.body.quoted_message_id || null;

        if (!sessionId || !groupId || !message) {
            logger.warn("Request body for group text message is missing", {
                body: req.body,
            });
            return res.status(400).json({
                success: false,
                message: "sessionId, groupId, and message are required",
            });
        }

        const formattedGroupId = validateGroupId(groupId);
        logger.info(`Sending text message to group: ${formattedGroupId}`);

        const socket = sessions.get(sessionId);
        if (!socket) {
            await handleMissingSession(sessionId);
            return res.status(404).json({
                success: false,
                message: "Session not found",
            });
        }

        const data = sessionData.get(sessionId);
        if (data?.status !== "connected") {
            await handleDisconnectedSession(
                sessionId,
                data?.status || "unknown",
            );
            return res.status(400).json({
                success: false,
                message: "Session not connected",
            });
        }

        // Parse mentions from text if mentions array is not provided
        const parsedMentions = mentions || parseMentions(message);

        // Build message options for Baileys
        const options = {};

        // Add quoted message if provided - retrieve actual message from store
        if (quotedMessageId) {
            try {
                const storedMessage = messageStore.get(quotedMessageId);

                if (storedMessage) {
                    // Transform message to proper quote format
                    let quotedMsg = storedMessage.message;

                    // Handle different message types for quotes
                    if (quotedMsg.text) {
                        quotedMsg = { conversation: quotedMsg.text };
                    } else if (quotedMsg.image) {
                        quotedMsg = { imageMessage: quotedMsg.image };
                    } else if (quotedMsg.video) {
                        quotedMsg = { videoMessage: quotedMsg.video };
                    } else if (quotedMsg.document) {
                        quotedMsg = { documentMessage: quotedMsg.document };
                    }

                    // Use the actual stored message for proper quote preview
                    options.quoted = {
                        key: storedMessage.key,
                        message: quotedMsg,
                    };
                } else {
                    // Fallback to stub if message not found in store
                    options.quoted = {
                        key: {
                            remoteJid: formattedGroupId,
                            id: quotedMessageId,
                            fromMe: false,
                            participant: formattedGroupId,
                        },
                        message: { conversation: "‎" },
                    };
                }
            } catch (e) {
                logger.error("Error creating quoted message for group", {
                    error: e.message,
                });
            }
        }

        // Add mentions if provided
        if (parsedMentions.length > 0) {
            options.mentionedJid = parsedMentions;
        }

        // Send message with options - use sendMessage utility to store the message
        const messageContent = { text: message };
        const result = await sendMessage(
            socket,
            formattedGroupId,
            messageContent,
            sessionId,
            Object.keys(options).length > 0 ? options : undefined,
        );

        res.json({
            success: true,
            messageId: result.key.id,
            groupId: formattedGroupId,
            timestamp: result.messageTimestamp,
            quoted: quotedMessageId || null,
            mentions: parsedMentions,
        });
    } catch (error) {
        logger.error("Error sending text message to group:", {
            error: error.message,
            stack: error.stack,
        });
        res.status(500).json({
            success: false,
            message: "Error sending text message to group",
            error: error.message || "Unknown error",
        });
    }
});

// NOTE: Button and list messages are not supported in Baileys v7
// Use text messages with numbered options as a workaround instead

// Send media message
app.post("/api/messages/media", upload.single("media"), async (req, res) => {
    // ============================================
    // CRITICAL: Set request timeout for large file uploads
    // This prevents Express from closing connection prematurely
    // ============================================
    res.setTimeout(600000, () => {
        // 10 minutes timeout
        logger.warn('Request timeout for /api/messages/media');
        if (!res.headersSent) {
            res.status(408).json({
                success: false,
                message: 'Request timeout - file upload took too long',
            });
        }
    });
    try {
        const { sessionId, to, type, caption } = req.body;
        const file = req.file;

        if (!sessionId || !to || !type || !file) {
            logger.warn("Request body for media message is missing", {
                body: req.body,
                file: req.file,
            });
            return res.status(400).json({
                success: false,
                message: "sessionId, to, type, and media are required",
            });
        }

        const formattedNumber = validateWhatsAppNumber(to);
        if (!formattedNumber) {
            logger.warn("Invalid number", { to });
            return res.status(400).json({
                success: false,
                message: "Invalid number",
            });
        }

        const socket = sessions.get(sessionId);
        if (!socket) {
            await handleMissingSession(sessionId);
            return res.status(404).json({
                success: false,
                message: "Session not found",
            });
        }

        const data = sessionData.get(sessionId);
        if (data?.status !== "connected") {
            await handleDisconnectedSession(
                sessionId,
                data?.status || "unknown",
            );
            return res.status(400).json({
                success: false,
                message: "Session not connected",
            });
        }

        let messageContent = {};
        const mediaBuffer = await fs.readFile(file.path);

        switch (type) {
            case "image":
                messageContent = {
                    image: mediaBuffer,
                    caption: caption || "",
                };
                break;
            case "audio":
                messageContent = {
                    audio: mediaBuffer,
                    mimetype: file.mimetype,
                };
                break;
            case "video":
                messageContent = {
                    video: mediaBuffer,
                    caption: caption || "",
                };
                break;
            case "document":
                messageContent = {
                    document: mediaBuffer,
                    mimetype: file.mimetype,
                    fileName: file.originalname,
                };
                break;
            default:
                logger.warn(`Message type not supported: ${type}`);
                return res.status(400).json({
                    success: false,
                    message: "Message type not supported",
                });
        }

        // console.log("🔁 Sending media message:", messageContent);

        const result = await socket.sendMessage(
            formattedNumber,
            messageContent,
        );

        if (caption && type === "document") {
            await socket.sendMessage(formattedNumber, { text: caption });
        }

        sessionData.set(sessionId, { ...data, lastActive: Date.now() });

        // Clean up temporary file
        await fs.unlink(file.path);

        res.json({
            success: true,
            messageId: result.key.id,
            timestamp: result.messageTimestamp,
        });
    } catch (error) {
        logger.error("Error sending media message:", {
            error: error.message,
            stack: error.stack,
        });

        // Clean up file in case of error
        if (req.file && req.file.path) {
            try {
                await fs.unlink(req.file.path);
            } catch (unlinkError) {
                logger.error("Error deleting temporary file:", unlinkError);
            }
        }

        res.status(500).json({
            success: false,
            message: "Error sending media message",
            error: error.message || "Unknown error",
        });
    }
});

// NEW ROUTES FOR BULK MESSAGES

// Send bulk messages (text)
app.post("/api/messages/bulk/text", async (req, res) => {
    try {
        const {
            sessionId,
            contacts,
            message,
            delay: messageDelay = 1000,
            mentions,
        } = req.body;

        if (
            !sessionId ||
            !contacts ||
            !Array.isArray(contacts) ||
            contacts.length === 0 ||
            !message
        ) {
            logger.warn("Request body for bulk text message is missing", {
                body: req.body,
            });
            return res.status(400).json({
                success: false,
                message:
                    "sessionId, contacts (array), and message are required",
            });
        }

        if (contacts.length > 1000) {
            return res.status(400).json({
                success: false,
                message: "Limit of 1000 contacts per bulk message",
            });
        }

        const socket = sessions.get(sessionId);
        if (!socket) {
            await handleMissingSession(sessionId);
            return res.status(404).json({
                success: false,
                message: "Session not found",
            });
        }

        const data = sessionData.get(sessionId);
        if (data?.status !== "connected") {
            await handleDisconnectedSession(
                sessionId,
                data?.status || "unknown",
            );
            return res.status(400).json({
                success: false,
                message: "Session not connected",
            });
        }

        // Create a unique job ID
        const jobId = `bulk_${sessionId}_${Date.now()}_${Math.random()
            .toString(36)
            .substr(2, 9)}`;

        const job = {
            id: jobId,
            sessionId,
            type: "text",
            message,
            contacts: contacts.slice(), // copie du tableau
            status: "pending",
            total: contacts.length,
            sent: 0,
            failed: 0,
            processed: 0,
            createdAt: Date.now(),
            startedAt: null,
            completedAt: null,
            error: null,
            mentions: mentions || null,
        };

        bulkMessageJobs.set(jobId, job);

        // Start processing in background
        setImmediate(() => {
            processBulkMessages(jobId, sessionId, contacts, message, {
                delay: messageDelay,
                type: "text",
                mentions,
            });
        });

        res.json({
            success: true,
            jobId,
            message: "Bulk message job created",
            total: contacts.length,
            mentions: mentions || [],
        });
    } catch (error) {
        logger.error("Error creating bulk text message job:", {
            error: error.message,
            stack: error.stack,
        });
        res.status(500).json({
            success: false,
            message: "Error creating bulk text message job",
            error: error.message || "Unknown error",
        });
    }
});

// Send bulk messages with media

app.post("/api/messages/media", async (req, res) => {
    try {
        const { sessionId, to, type, caption, mediaUrl } = req.body;

        if (!sessionId || !to || !type || !mediaUrl) {
            return res.status(400).json({
                success: false,
                message: "sessionId, to, type, and mediaUrl are required",
            });
        }

        const formattedNumber = validateWhatsAppNumber(to);
        if (!formattedNumber) {
            return res.status(400).json({
                success: false,
                message: "Invalid number",
            });
        }

        const socket = sessions.get(sessionId);
        if (!socket) {
            await handleMissingSession(sessionId);
            return res.status(404).json({
                success: false,
                message: "Session not found",
            });
        }

        const data = sessionData.get(sessionId);
        if (data?.status !== "connected") {
            await handleDisconnectedSession(
                sessionId,
                data?.status || "unknown",
            );
            return res.status(400).json({
                success: false,
                message: "Session not connected",
            });
        }

        // Download media from URL
        const response = await fetch(mediaUrl);
        if (!response.ok) {
            return res.status(400).json({
                success: false,
                message: "Failed to download media from URL",
            });
        }

        const mediaBuffer = Buffer.from(await response.arrayBuffer());
        const contentType = response.headers.get("content-type");
        const fileName = mediaUrl.split("/").pop();

        let messageContent = {};

        switch (type) {
            case "image":
                messageContent = {
                    image: mediaBuffer,
                    caption: caption || "",
                };
                break;
            case "audio":
                messageContent = {
                    audio: mediaBuffer,
                    mimetype: contentType,
                };
                break;
            case "video":
                messageContent = {
                    video: mediaBuffer,
                    caption: caption || "",
                };
                break;
            case "document":
                messageContent = {
                    document: mediaBuffer,
                    mimetype: contentType,
                    fileName,
                };
                break;
            default:
                return res.status(400).json({
                    success: false,
                    message: "Message type not supported",
                });
        }

        const result = await socket.sendMessage(
            formattedNumber,
            messageContent,
        );
        sessionData.set(sessionId, { ...data, lastActive: Date.now() });

        res.json({
            success: true,
            messageId: result.key.id,
            timestamp: result.messageTimestamp,
        });
    } catch (error) {
        logger.error("Error sending media message via URL:", {
            error: error.message,
            stack: error.stack,
        });

        res.status(500).json({
            success: false,
            message: "Error sending media message",
            error: error.message || "Unknown error",
        });
    }
});

// Send media message to group
app.post("/api/messages/group/media", async (req, res) => {
    try {
        const { sessionId, groupId, type, caption, mediaUrl } = req.body;

        if (!sessionId || !groupId || !type || !mediaUrl) {
            return res.status(400).json({
                success: false,
                message: "sessionId, groupId, type, and mediaUrl are required",
            });
        }

        const formattedGroupId = validateGroupId(groupId);
        logger.info(`Sending media message to group: ${formattedGroupId}`);

        const socket = sessions.get(sessionId);
        if (!socket) {
            await handleMissingSession(sessionId);
            return res.status(404).json({
                success: false,
                message: "Session not found",
            });
        }

        const data = sessionData.get(sessionId);
        if (data?.status !== "connected") {
            await handleDisconnectedSession(
                sessionId,
                data?.status || "unknown",
            );
            return res.status(400).json({
                success: false,
                message: "Session not connected",
            });
        }

        // Download media from URL
        const response = await fetch(mediaUrl);
        if (!response.ok) {
            return res.status(400).json({
                success: false,
                message: "Failed to download media from URL",
            });
        }

        const mediaBuffer = Buffer.from(await response.arrayBuffer());
        const contentType = response.headers.get("content-type");
        const fileName = mediaUrl.split("/").pop();

        let messageContent = {};

        switch (type) {
            case "image":
                messageContent = {
                    image: mediaBuffer,
                    caption: caption || "",
                };
                break;
            case "audio":
                messageContent = {
                    audio: mediaBuffer,
                    mimetype: contentType,
                };
                break;
            case "video":
                messageContent = {
                    video: mediaBuffer,
                    caption: caption || "",
                };
                break;
            case "document":
                messageContent = {
                    document: mediaBuffer,
                    mimetype: contentType,
                    fileName,
                };
                break;
            default:
                return res.status(400).json({
                    success: false,
                    message: "Message type not supported",
                });
        }

        const result = await socket.sendMessage(
            formattedGroupId,
            messageContent,
        );
        sessionData.set(sessionId, { ...data, lastActive: Date.now() });

        res.json({
            success: true,
            messageId: result.key.id,
            groupId: formattedGroupId,
            timestamp: result.messageTimestamp,
        });
    } catch (error) {
        logger.error("Error sending media message to group:", {
            error: error.message,
            stack: error.stack,
        });

        res.status(500).json({
            success: false,
            message: "Error sending media message to group",
            error: error.message || "Unknown error",
        });
    }
});

//  Get bulk message job status
app.get("/api/messages/bulk/:jobId/status", (req, res) => {
    try {
        const { jobId } = req.params;

        const job = bulkMessageJobs.get(jobId);
        if (!job) {
            return res.status(404).json({
                success: false,
                message: "Job not found",
            });
        }

        const progress =
            job.total > 0 ? Math.round((job.processed / job.total) * 100) : 0;

        res.json({
            success: true,
            job: {
                id: job.id,
                sessionId: job.sessionId,
                type: job.type,
                status: job.status,
                total: job.total,
                sent: job.sent,
                failed: job.failed,
                processed: job.processed,
                progress: `${progress}%`,
                createdAt: new Date(job.createdAt).toISOString(),
                startedAt: job.startedAt
                    ? new Date(job.startedAt).toISOString()
                    : null,
                completedAt: job.completedAt
                    ? new Date(job.completedAt).toISOString()
                    : null,
                error: job.error,
            },
        });
    } catch (error) {
        logger.error("Error getting bulk message job status:", error);
        res.status(500).json({
            success: false,
            message: "Error getting bulk message job status",
            error: error.message || "Unknown error",
        });
    }
});

// Cancel a bulk job
app.delete("/api/messages/bulk/:jobId", (req, res) => {
    try {
        const { jobId } = req.params;

        const job = bulkMessageJobs.get(jobId);
        if (!job) {
            return res.status(404).json({
                success: false,
                message: "Job not found",
            });
        }

        if (
            job.status === "completed" ||
            job.status === "completed_with_errors" ||
            job.status === "failed"
        ) {
            return res.status(400).json({
                success: false,
                message: "The job is already completed and cannot be cancelled",
            });
        }

        job.status = "cancelled";
        logger.info(`Job bulk ${jobId} cancelled`);

        res.json({
            success: true,
            message: "Job cancelled successfully",
        });
    } catch (error) {
        logger.error("Error cancelling bulk job:", error);
        res.status(500).json({
            success: false,
            message: "Error cancelling bulk job",
            error: error.message || "Unknown error",
        });
    }
});

// List all bulk jobs
app.get("/api/messages/bulk", (req, res) => {
    try {
        const { sessionId, status, limit = 50 } = req.query;

        let jobs = Array.from(bulkMessageJobs.values());

        // Filter by sessionId if specified
        if (sessionId) {
            jobs = jobs.filter((job) => job.sessionId === sessionId);
        }

        // Filter by status if specified
        if (status) {
            jobs = jobs.filter((job) => job.status === status);
        }

        // Limit the number of results
        jobs = jobs.slice(0, parseInt(limit));

        // Sort by creation date (most recent first)
        jobs.sort((a, b) => b.createdAt - a.createdAt);

        const formattedJobs = jobs.map((job) => {
            const progress =
                job.total > 0
                    ? Math.round((job.processed / job.total) * 100)
                    : 0;
            return {
                id: job.id,
                sessionId: job.sessionId,
                type: job.type,
                status: job.status,
                total: job.total,
                sent: job.sent,
                failed: job.failed,
                processed: job.processed,
                progress: `${progress}%`,
                createdAt: new Date(job.createdAt).toISOString(),
                startedAt: job.startedAt
                    ? new Date(job.startedAt).toISOString()
                    : null,
                completedAt: job.completedAt
                    ? new Date(job.completedAt).toISOString()
                    : null,
            };
        });

        res.json({
            success: true,
            jobs: formattedJobs,
            total: jobs.length,
        });
    } catch (error) {
        logger.error("Error getting bulk jobs:", error);
        res.status(500).json({
            success: false,
            message: "Error getting bulk jobs",
            error: error.message || "Unknown error",
        });
    }
});

// NEW ROUTES FOR SCHEDULED MESSAGES

// Schedule a text message
app.post("/api/messages/schedule/text", async (req, res) => {
    try {
        const { sessionId, to, message, scheduledAt } = req.body;

        if (!sessionId || !to || !message || !scheduledAt) {
            logger.warn("Request body for scheduled text message is missing", {
                body: req.body,
            });
            return res.status(400).json({
                success: false,
                message: "sessionId, to, message, and scheduledAt are required",
            });
        }

        //  Validate the date format
        const scheduledDate = new Date(scheduledAt);
        if (isNaN(scheduledDate.getTime())) {
            return res.status(400).json({
                success: false,
                message: "Invalid date for scheduledAt",
            });
        }

        // Verify that the date is in the future
        if (scheduledDate.getTime() <= Date.now()) {
            return res.status(400).json({
                success: false,
                message: "Scheduled date must be in the future",
            });
        }

        // Verify phone number
        const formattedNumber = validateWhatsAppNumber(to);
        if (!formattedNumber) {
            logger.warn("Invalid number", { to });
            return res.status(400).json({
                success: false,
                message: "Invalid number",
            });
        }

        //  Verify that the session exists
        const socket = sessions.get(sessionId);
        if (!socket) {
            await handleMissingSession(sessionId);
            return res.status(404).json({
                success: false,
                message: "Session not found",
            });
        }

        // Create a unique ID for the scheduled message
        const messageId = `scheduled_${sessionId}_${Date.now()}_${Math.random()
            .toString(36)
            .substr(2, 9)}`;

        const scheduledMessage = {
            id: messageId,
            sessionId,
            to: formattedNumber,
            message,
            type: "text",
            status: "pending",
            scheduledAt: scheduledDate.getTime(),
            createdAt: Date.now(),
            sentAt: null,
            error: null,
            messageId: null,
        };

        scheduledMessages.set(messageId, scheduledMessage);

        logger.info(
            `Scheduled text message created: ${messageId} for ${scheduledDate.toISOString()}`,
        );

        res.json({
            success: true,
            messageId,
            scheduledAt: scheduledDate.toISOString(),
            message: "Scheduled text message created successfully",
        });
    } catch (error) {
        logger.error("Error scheduling text message:", {
            error: error.message,
            stack: error.stack,
        });
        res.status(500).json({
            success: false,
            message: "Error scheduling text message",
            error: error.message || "Unknown error",
        });
    }
});

//  Schedule a media message
app.post(
    "/api/messages/schedule/media",
    upload.single("media"),
    async (req, res) => {
        // ============================================
        // CRITICAL: Set request timeout for large file uploads
        // ============================================
        res.setTimeout(600000, () => {
            // 10 minutes timeout
            logger.warn('Request timeout for /api/messages/schedule/media');
            if (!res.headersSent) {
                res.status(408).json({
                    success: false,
                    message: 'Request timeout - file upload took too long',
                });
            }
        });
        try {
            const { sessionId, to, type, caption, scheduledAt } = req.body;
            const file = req.file;

            if (!sessionId || !to || !type || !scheduledAt || !file) {
                logger.warn(
                    "Request body for scheduled media message is missing",
                    {
                        body: req.body,
                    },
                );
                return res.status(400).json({
                    success: false,
                    message:
                        "sessionId, to, type, scheduledAt, and media are required",
                });
            }

            //  Validate the date format
            const scheduledDate = new Date(scheduledAt);
            if (isNaN(scheduledDate.getTime())) {
                return res.status(400).json({
                    success: false,
                    message: "Invalid date for scheduledAt",
                });
            }

            // Verify that the date is in the future
            if (scheduledDate.getTime() <= Date.now()) {
                return res.status(400).json({
                    success: false,
                    message: "Scheduled date must be in the future",
                });
            }

            // Verify phone number
            const formattedNumber = validateWhatsAppNumber(to);
            if (!formattedNumber) {
                logger.warn("Invalid number", { to });
                return res.status(400).json({
                    success: false,
                    message: "Invalid number",
                });
            }

            // Verify that the session exists
            const socket = sessions.get(sessionId);
            if (!socket) {
                await handleMissingSession(sessionId);
                return res.status(404).json({
                    success: false,
                    message: "Session not found",
                });
            }

            // Verify that the session exists
            const mediaBuffer = await fs.readFile(file.path);

            // Create a unique ID for the scheduled message
            const messageId = `scheduled_media_${sessionId}_${Date.now()}_${Math.random()
                .toString(36)
                .substr(2, 9)}`;

            const scheduledMessage = {
                id: messageId,
                sessionId,
                to: formattedNumber,
                message: caption || "",
                type: type,
                status: "pending",
                scheduledAt: scheduledDate.getTime(),
                createdAt: Date.now(),
                sentAt: null,
                error: null,
                messageId: null,
                options: {
                    mediaBuffer,
                    mimetype: file.mimetype,
                    fileName: file.originalname,
                    caption: caption || "",
                },
            };

            scheduledMessages.set(messageId, scheduledMessage);

            // Clean up the temporary file
            await fs.unlink(file.path);

            logger.info(
                `Scheduled media message created: ${messageId} for ${scheduledDate.toISOString()}`,
            );

            res.json({
                success: true,
                messageId,
                scheduledAt: scheduledDate.toISOString(),
                message: "Scheduled media message created successfully",
            });
        } catch (error) {
            logger.error("Error scheduling media message:", {
                error: error.message,
                stack: error.stack,
            });

            // Clean up the file in case of error
            if (req.file && req.file.path) {
                try {
                    await fs.unlink(req.file.path);
                } catch (unlinkError) {
                    logger.error("Error deleting temporary file:", unlinkError);
                }
            }

            res.status(500).json({
                success: false,
                message: "Error scheduling media message",
                error: error.message || "Unknown error",
            });
        }
    },
);

//  Get the status of a scheduled message
app.get("/api/messages/schedule/:messageId/status", (req, res) => {
    try {
        const { messageId } = req.params;

        const scheduledMsg = scheduledMessages.get(messageId);
        if (!scheduledMsg) {
            return res.status(404).json({
                success: false,
                message: "Scheduled message not found",
            });
        }

        res.json({
            success: true,
            message: {
                id: scheduledMsg.id,
                sessionId: scheduledMsg.sessionId,
                to: scheduledMsg.to,
                type: scheduledMsg.type,
                status: scheduledMsg.status,
                scheduledAt: new Date(scheduledMsg.scheduledAt).toISOString(),
                createdAt: new Date(scheduledMsg.createdAt).toISOString(),
                sentAt: scheduledMsg.sentAt
                    ? new Date(scheduledMsg.sentAt).toISOString()
                    : null,
                error: scheduledMsg.error,
                messageId: scheduledMsg.messageId,
            },
        });
    } catch (error) {
        logger.error("Error getting scheduled message status:", error);
        res.status(500).json({
            success: false,
            message: "Error getting scheduled message status",
            error: error.message || "Unknown error",
        });
    }
});

// Cancel a scheduled message
app.delete("/api/messages/schedule/:messageId", (req, res) => {
    try {
        const { messageId } = req.params;

        const scheduledMsg = scheduledMessages.get(messageId);
        if (!scheduledMsg) {
            return res.status(404).json({
                success: false,
                message: "Scheduled message not found",
            });
        }

        if (scheduledMsg.status !== "pending") {
            return res.status(400).json({
                success: false,
                message:
                    "Cannot cancel a message that is already sent or failed",
            });
        }

        scheduledMessages.delete(messageId);
        logger.info(`Message scheduled ${messageId} cancelled`);

        res.json({
            success: true,
            message: "Scheduled message cancelled successfully",
        });
    } catch (error) {
        logger.error("Error cancelling scheduled message:", error);
        res.status(500).json({
            success: false,
            message: "Error cancelling scheduled message",
            error: error.message || "Unknown error",
        });
    }
});

// List all scheduled messages
app.get("/api/messages/schedule", (req, res) => {
    try {
        const { sessionId, status, limit = 50 } = req.query;

        let messages = Array.from(scheduledMessages.values());

        // Filter by sessionId if specified
        if (sessionId) {
            messages = messages.filter((msg) => msg.sessionId === sessionId);
        }

        // Filter by status if specified
        if (status) {
            messages = messages.filter((msg) => msg.status === status);
        }

        // Limit the number of results
        messages = messages.slice(0, parseInt(limit));

        // Sort by scheduled date
        messages.sort((a, b) => a.scheduledAt - b.scheduledAt);

        const formattedMessages = messages.map((msg) => ({
            id: msg.id,
            sessionId: msg.sessionId,
            to: msg.to,
            type: msg.type,
            status: msg.status,
            scheduledAt: new Date(msg.scheduledAt).toISOString(),
            createdAt: new Date(msg.createdAt).toISOString(),
            sentAt: msg.sentAt ? new Date(msg.sentAt).toISOString() : null,
            error: msg.error,
        }));

        res.json({
            success: true,
            messages: formattedMessages,
            total: messages.length,
        });
    } catch (error) {
        logger.error("Error getting scheduled messages:", error);
        res.status(500).json({
            success: false,
            message: "Error getting scheduled messages",
            error: error.message || "Unknown error",
        });
    }
});

// Get contacts
app.get("/api/sessions/:sessionId/contacts", async (req, res) => {
    try {
        const { sessionId } = req.params;

        const socket = sessions.get(sessionId);
        if (!socket) {
            await handleMissingSession(sessionId);
            return res.status(404).json({
                success: false,
                message: "Session not found",
            });
        }

        const data = sessionData.get(sessionId);
        if (data?.status !== "connected") {
            await handleDisconnectedSession(
                sessionId,
                data?.status || "unknown",
            );
            return res.status(400).json({
                success: false,
                message: "Session not connected",
            });
        }

        // Retrieve contacts from WhatsApp metadata
        const contacts = [];
        try {
            const chats = await socket.groupFetchAllParticipating();
            for (const chatId in chats) {
                const chat = chats[chatId];
                if (chat.participants) {
                    chat.participants.forEach((participant) => {
                        contacts.push({
                            jid: participant.id,
                            name:
                                participant.name ||
                                participant.id.split("@")[0],
                            isAdmin: participant.isAdmin,
                        });
                    });
                }
            }
        } catch (error) {
            logger.error("Error getting contacts:", error);
        }

        res.json({
            success: true,
            contacts,
        });
    } catch (error) {
        logger.error("Error getting contacts:", {
            error: error.message,
            stack: error.stack,
        });
        res.status(500).json({
            success: false,
            message: "Error getting contacts",
            error: error.message || "Unknown error",
        });
    }
});

// Get groups for a session
app.get("/api/sessions/:sessionId/groups", async (req, res) => {
    try {
        const { sessionId } = req.params;

        const socket = sessions.get(sessionId);
        if (!socket) {
            await handleMissingSession(sessionId);
            return res.status(404).json({
                success: false,
                message: "Session not found",
            });
        }

        const data = sessionData.get(sessionId);
        if (data?.status !== "connected") {
            await handleDisconnectedSession(
                sessionId,
                data?.status || "unknown",
            );
            return res.status(400).json({
                success: false,
                message: "Session not connected",
            });
        }

        // Retrieve groups from WhatsApp metadata
        const groups = [];
        try {
            const chats = await socket.groupFetchAllParticipating();

            for (const groupId in chats) {
                const chat = chats[groupId];

                // Get group metadata
                try {
                    const metadata = await socket.groupMetadata(groupId);

                    groups.push({
                        id: groupId,
                        name: metadata.subject || "Unknown Group",
                        participants: metadata.participants?.length || 0,
                        isAdmin: metadata?.owner === socket.user?.id,
                        creationDate: metadata.creation
                            ? new Date(metadata.creation * 1000).toISOString()
                            : null,
                        desc: metadata.desc || null,
                    });
                } catch (metadataError) {
                    // Fallback if metadata fetch fails
                    groups.push({
                        id: groupId,
                        name: chat.subject || "Unknown Group",
                        participants: chat.participants?.length || 0,
                    });
                }
            }

            logger.info(
                `Found ${groups.length} groups for session ${sessionId}`,
            );
        } catch (error) {
            logger.error("Error getting groups:", error);
        }

        res.json({
            success: true,
            groups,
            total: groups.length,
        });
    } catch (error) {
        logger.error("Error getting groups:", {
            error: error.message,
            stack: error.stack,
        });
        res.status(500).json({
            success: false,
            message: "Error getting groups",
            error: error.message || "Unknown error",
        });
    }
});

// Disconnect session
app.delete("/api/sessions/:sessionId", async (req, res) => {
    try {
        const { sessionId } = req.params;
        const session_device_id = extractSessionId(sessionId);

        const socket = sessions.get(sessionId);
        if (socket) {
            await socket.logout();
            sessions.delete(sessionId);
            await fs.rm(getSessionDir(sessionId), {
                recursive: true,
                force: true,
            });
        }

        sessionData.set(sessionId, { status: "disconnected" });

        // Update device status to disconnected
        await updateDeviceStatus(session_device_id, "disconnected");

        // Update session status to disconnected
        await updateSessionStatus(sessionId, {
            status: "disconnected",
        });

        res.json({
            success: true,
            message: "Session disconnected successfully",
        });
    } catch (error) {
        logger.error("Error disconnecting session:", {
            error: error.message,
            stack: error.stack,
        });
        res.status(500).json({
            success: false,
            message: "Error disconnecting session",
            error: error.message || "Unknown error",
        });
    }
});

// Error handling middleware
app.use((error, req, res, next) => {
    logger.error("Unhandled error:", {
        error: error.message,
        stack: error.stack,
    });
    res.status(500).json({
        success: false,
        message: "Internal server error",
        error: error.message || "Unknown error",
    });
});

// Start server
app.listen(PORT, async () => {
    logger.info(`Server started on port ${PORT}`);

    // Create sessions and uploads directories
    try {
        await fs.mkdir(getSessionsBaseDir(), { recursive: true });
        await fs.mkdir(path.join(__dirname, "uploads"), { recursive: true });
        logger.debug("Sessions and uploads directories created");
    } catch (error) {
        logger.error("Error creating directories:", error);
    }

    // ============================================
    // CREDENTIALS HEALTH CHECK (Before loading sessions)
    // Ini adalah kunci untuk "Scan Sekali, Selamanya"
    // ============================================
    logger.info("🏥 Running credentials health check...");
    const healthCheckResult = await healthCheckAllCredentials();

    if (healthCheckResult.total > 0) {
        logger.info(`📊 Credentials health check results:`);
        logger.info(`   Total sessions: ${healthCheckResult.total}`);
        logger.info(`   Valid: ${healthCheckResult.valid}`);
        logger.info(`   Auto-recovered: ${healthCheckResult.recovered}`);
        logger.info(`   Corrupted (unrecoverable): ${healthCheckResult.corrupted}`);

        if (healthCheckResult.recovered > 0) {
            logger.info(
                `✅ Successfully auto-recovered ${healthCheckResult.recovered} corrupted session(s)!`,
            );
        }

        if (healthCheckResult.corrupted > 0) {
            logger.error(
                `⚠️  ${healthCheckResult.corrupted} session(s) have unrecoverable credentials`,
            );

            // List corrupted sessions
            healthCheckResult.sessions
                .filter((s) => !s.valid && !s.recovered)
                .forEach((s) => {
                    logger.error(`   - ${s.sessionId}: ${s.error}`);
                });

            // ============================================
            // AUTO-CLEANUP CORRUPTED SESSIONS
            // Hapus session yang rusak TANPA backup untuk mencegah:
            // 1. Storage pollution (folder tidak berguna)
            // 2. Error berulang setiap server restart
            // 3. Log pollution
            // ============================================
            logger.info(`🧹 Cleaning up ${healthCheckResult.corrupted} unrecoverable session(s)...`);
            const cleanupResult = await cleanupCorruptedSessions();

            if (cleanupResult.cleaned > 0) {
                logger.info(
                    `✅ Cleaned up ${cleanupResult.cleaned} corrupted session folder(s):`
                );
                cleanupResult.cleanedSessions.forEach((sessionId) => {
                    logger.info(`   🗑️ Deleted: ${sessionId}`);
                });

                // Update device status untuk sessions yang dihapus
                for (const sessionId of cleanupResult.cleanedSessions) {
                    const session_device_id = extractSessionId(sessionId);
                    try {
                        await query(
                            `UPDATE devices SET status = $1 WHERE session_device_id = $2`,
                            ["disconnected", session_device_id]
                        );
                        await query(
                            `UPDATE whatsapp_sessions SET status = $1 WHERE session_id = $2`,
                            ["disconnected", sessionId]
                        );
                        logger.info(`   Updated device status: ${session_device_id} → disconnected`);
                    } catch (error) {
                        logger.error(`   Failed to update device status for ${session_device_id}:`, error);
                    }
                }
            }

            if (cleanupResult.errors.length > 0) {
                logger.error(`⚠️ Errors during cleanup:`);
                cleanupResult.errors.forEach((err) => logger.error(`   - ${err}`));
            }
        }
    }

    // ============================================
    // APP STATE CLEANUP (Storage Maintenance)
    // Mencegah session folder tumbuh terlalu besar
    // Membersihkan session files yang lama secara AMAN
    // ============================================
    logger.info("🧹 Running session storage maintenance...");
    const maintenanceResult = await cleanupAllSessions();

    if (maintenanceResult.totalSessions > 0) {
        logger.info(`📊 Storage maintenance results:`);
        logger.info(`   Total sessions analyzed: ${maintenanceResult.totalSessions}`);
        logger.info(`   Sessions cleaned: ${maintenanceResult.cleanedSessions}`);
        logger.info(`   Total storage before: ${maintenanceResult.totalBeforeBytes ? ((maintenanceResult.totalBeforeBytes / (1024 * 1024)).toFixed(2) + " MB") : "N/A"}`);
        logger.info(`   Total storage after: ${maintenanceResult.totalAfterBytes ? ((maintenanceResult.totalAfterBytes / (1024 * 1024)).toFixed(2) + " MB") : "N/A"}`);

        if (maintenanceResult.totalFreedBytes > 0) {
            const freedMB = (maintenanceResult.totalFreedBytes / (1024 * 1024)).toFixed(2);
            logger.info(`   💾 Total space freed: ${freedMB} MB`);
        }

        // Show large sessions warning
        if (maintenanceResult.largeSessions && maintenanceResult.largeSessions.length > 0) {
            logger.warn(`⚠️ Large sessions detected (>10MB):`);
            maintenanceResult.largeSessions.forEach((s) => {
                logger.warn(`   - ${s.sessionId}: ${s.size} (${s.fileCount?.total || 0} files total)`);
                logger.warn(`     Session cache: ${s.fileCount?.session || 0} files`);
                logger.warn(`     Sender keys: ${s.fileCount?.senderKey || 0} files (protected)`);
                logger.warn(`     LID mappings: ${s.fileCount?.lidMapping || 0} files (protected)`);
            });
            logger.info(`   💡 Tip: Large session size is usually due to many chat contacts/groups.`);
            logger.info(`   This is NORMAL and doesn't affect functionality. Only old cache files are cleaned.`);
        }

        // Show details for sessions that were cleaned
        if (maintenanceResult.cleanedSessions > 0) {
            logger.info(`🗑️ Cleaned sessions (${maintenanceResult.cleanedSessions}):`);
            maintenanceResult.details
                .filter((d) => d.cleaned > 0 || d.freedBytes > 0)
                .forEach((detail) => {
                    logger.info(`   - ${detail.sessionId}:`);
                    logger.info(`     Before: ${detail.before} → After: ${detail.after}`);
                    logger.info(`     Freed: ${detail.freed} (${detail.cleaned} files cleaned)`);
                });
        }
    }

    // Load existing sessions
    await loadExistingSessions();

    // Wait a bit for sessions to stabilize before verification
    logger.info("⏳ Waiting 10 seconds for sessions to stabilize before verification...");
    await delay(10000);

    // Verify device status matches actual sessions (after restart)
    await verifyDeviceStatusOnStartup();

    // Start cleanup of inactive sessions
    cleanupInactiveSessions();

    // Start connection health monitoring
    monitorConnectionHealth();
});

// ENHANCED Graceful shutdown with proper cleanup
process.on("SIGINT", async () => {
    logger.info("🛑 SIGINT received - Starting graceful shutdown...");

    // Set flag to prevent new operations
    process.isShuttingDown = true;

    const shutdownStart = Date.now();
    let sessionsDisconnected = 0;
    let sessionsFailed = 0;

    // Flush inbound message buffer before shutting down
    logger.info("📝 Flushing inbound message buffer...");
    await forceFlushInbound();

    // Close all sockets gracefully WITHOUT logging out.
    // socket.logout() would call remove-companion-device, which invalidates
    // the WhatsApp session server-side and forces a fresh QR scan on restart.
    // socket.end() just closes the WebSocket; the auth_info_*.json files on
    // disk remain valid and useMultiFileAuthState picks them up on next boot.
    // We also leave the DB status untouched so the device doesn't flap to
    // "disconnected" during a restart — it stays "connected" and is confirmed
    // by the connection.update("open") handler once recovery completes.
    for (const [sessionId, socket] of sessions) {
        try {
            logger.info(`📱 Closing socket for session ${sessionId}...`);
            await socket.end(undefined);
            sessionsDisconnected++;
            logger.info(`✅ Session ${sessionId} socket closed`);
        } catch (error) {
            sessionsFailed++;
            logger.error(
                `❌ Error closing socket for ${sessionId}:`,
                error.message,
            );
        }
    }

    // Clear all stores
    sessions.clear();
    sessionData.clear();
    scheduledMessages.clear();
    bulkMessageJobs.clear();
    messageStore.clear();

    const shutdownDuration = Math.round((Date.now() - shutdownStart) / 1000);

    logger.info(`🏁 Shutdown complete:`);
    logger.info(`   - Sessions disconnected: ${sessionsDisconnected}`);
    logger.info(`   - Sessions failed: ${sessionsFailed}`);
    logger.info(`   - Duration: ${shutdownDuration}s`);

    process.exit(sessionsFailed > 0 ? 1 : 0);
});

// Handle SIGTERM (for docker, k8s, etc)
process.on("SIGTERM", async () => {
    logger.info("🛑 SIGTERM received - Starting graceful shutdown...");

    // Set flag to prevent new operations
    process.isShuttingDown = true;

    const shutdownStart = Date.now();

    // Flush inbound message buffer before shutting down
    logger.info("📝 Flushing inbound message buffer...");
    await forceFlushInbound();

    // Close sockets gracefully without logout (see SIGINT handler for why).
    // Leaves credentials on disk intact so loadExistingSessions() can recover
    // them on the next boot. We also leave DB status untouched so the device
    // doesn't flap to "disconnected" during a brief restart.
    const disconnectPromises = [];
    for (const [sessionId, socket] of sessions) {
        disconnectPromises.push(
            (async () => {
                try {
                    await socket.end(undefined);
                    logger.info(`✅ Session ${sessionId} socket closed`);
                } catch (error) {
                    logger.error(
                        `❌ Error closing socket for ${sessionId}:`,
                        error.message,
                    );
                }
            })(),
        );
    }

    // Wait for disconnections with 10s timeout
    await Promise.race([
        Promise.allSettled(disconnectPromises),
        new Promise((resolve) => setTimeout(resolve, 10000)),
    ]);

    // Clear all stores
    sessions.clear();
    sessionData.clear();
    scheduledMessages.clear();
    bulkMessageJobs.clear();
    messageStore.clear();

    logger.info(
        `🏁 Graceful shutdown complete (${Math.round((Date.now() - shutdownStart) / 1000)}s)`,
    );
    process.exit(0);
});

// Handle uncaught exceptions
process.on("uncaughtException", (error) => {
    const errorCode = error?.code;

    // Don't crash on network/webhook errors - just log and continue
    const nonFatalCodes = [
        "ECONNREFUSED",
        "ENOTFOUND",
        "ETIMEDOUT",
        "ECONNRESET",
        "EPIPE",
    ];

    if (nonFatalCodes.includes(errorCode)) {
        logger.error({
            msg: "⚠️ Uncaught network error (non-fatal, continuing)",
            error: error?.message,
            code: errorCode,
            stack: error?.stack,
        });
        return; // Don't exit
    }

    // For other errors, log and exit with delay
    logger.error({
        msg: "💥 Uncaught Exception (fatal)",
        error: error?.message,
        code: errorCode,
        stack: error?.stack,
    });

    // Try to save state before exiting
    setTimeout(() => process.exit(1), 5000);
});

// Handle unhandled promise rejections
process.on("unhandledRejection", (reason, promise) => {
    const errorCode = reason?.code;

    // Don't crash on network/webhook errors - just log and continue
    const nonFatalCodes = [
        "ECONNREFUSED",
        "ENOTFOUND",
        "ETIMEDOUT",
        "ECONNRESET",
        "EPIPE",
    ];

    if (nonFatalCodes.includes(errorCode)) {
        logger.error({
            msg: "⚠️ Unhandled network rejection (non-fatal, continuing)",
            error: reason?.message || reason,
            code: errorCode,
        });
        return; // Don't exit
    }

    // For other errors, log and exit with delay
    logger.error({
        msg: "💥 Unhandled Promise Rejection (fatal)",
        error: reason?.message || reason,
        code: errorCode,
        reason: reason,
    });

    // Try to save state before exiting
    setTimeout(() => process.exit(1), 5000);
});
